import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useKiosk } from "@screen/Kiosk/useKiosk";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser, Address } from "@utils/store/user/useUser";
import appToast from "@components/Toast";

import "./onboarding.css";
import { SupplierContext } from "./SupplierContext";
import LocateSheet from "./components/LocateSheet";
import { resolveLocateGate } from "@components/address/func/locatePermission";
import { getCurrentLocation, geocodeToAddress, combinedAddress } from "@components/address/func/addressTool";
import { getSupplier, Supplier, SUPPLIERS } from "./suppliers";
import { fetchSupplierByShopCode, fetchCylinderByBarcode, fetchCustomerContexts, matchByPhone, matchSuppliersByPhone, matchByLandline, matchSuppliersByLandline, matchByAddress, matchSuppliersByAddress, recommendSuppliers, registerWalkIn, registerUnboundCustomer, refreshCustomerSession, getRegisterApiErrorMessage, entryCheck, EntryCheckData, MatchedSupplierApi, MatchedSupplierByAddressApi, WalkInAddress, SupplierByShopCode } from "./registerApi";
import { addressToGeocode } from "@components/address/func/addressTool";
import { pickChooseIdentity } from "@utils/func/identity";
import type { IdentityContext } from "@utils/store/user/useUser";
import { clearShopInviteCode } from "@utils/shopInviteCode";
import { resolveBarcode, isExistingPhone, findCustomerByPhone, CylinderInfo, CylinderCustomer, toMatchedCustomer } from "./demoData";
import { isPracticeMode } from "@utils/practice/practiceMode";
import { useAuthReady } from "@provider/LineLoginProvider";
import { PRACTICE_SUPPLIER_ID, PRACTICE_ADDRESS_ID, PRACTICE_ADDRESS_FULL } from "@utils/practice/practiceOrder";
import { practiceSupplierName, practiceSupplierPhone } from "@utils/practice/practiceMerchant";

/**
 * 進 kiosk / gasShop 前要帶入的「所屬瓦斯行」。onboarding 只把地址寫進 chooseIdentity，
 * 卻沒帶 primary_supplier；kiosk 的 useKioskBootstrap 因 `!supplierId` 直接跳出 → 商品空白、
 * 草稿建立不了，走到結算頁會被「空車守衛」踢回 kiosk 首頁。這裡補上瓦斯行讓下單流程完整。
 * 練習模式必須用 PRACTICE_SUPPLIER_ID，bootstrap 的 available_suppliers 才對得上（否則 find 不到）。
 */
const buildPrimarySupplier = (sid?: string | null) => {
  if (isPracticeMode()) {
    return { supplier_id: PRACTICE_SUPPLIER_ID, supplier_name: practiceSupplierName(), is_open_now: true, phone: practiceSupplierPhone() };
  }
  const s = getSupplier(sid);
  return { supplier_id: sid || "S001", supplier_name: s.name, is_open_now: true, phone: s.phone };
};

import Screen from "./components/Screen";
import QrBarcode from "./pages/QrBarcode";
import AdKnown from "./pages/AdKnown";
import ExistingPhone from "./pages/ExistingPhone";
import SupplierPhone from "./pages/SupplierPhone";
import NpPhone from "./pages/np/NpPhone";
import NpOtp from "./pages/np/NpOtp";
import NpConfirmSupplier from "./pages/np/NpConfirmSupplier";
import NpAutoMatched from "./pages/np/NpAutoMatched";
import NpAsk from "./pages/np/NpAsk";
import NpHomePhone from "./pages/np/NpHomePhone";
import NpAddress from "./pages/np/NpAddress";
import NpPickSupplier from "./pages/np/NpPickSupplier";
import NpDone, { NpDoneVariant } from "./pages/np/NpDone";
import { isKnownAddress, findSuppliersByPhone, MatchedSupplier } from "./demoData";
import { trackGa } from "@lib/tracker";

type OnbScreen =
  | "home"
  | "qrGate"
  | "qrBarcode"
  | "qrOtp"
  | "adKnown"
  | "existingPhone"
  | "supplierPhone"
  | "npPhone"
  | "npOtp"
  | "npPickSupplier"
  | "npConfirmSupplier"
  | "npAsk"
  | "npHomePhone"
  | "npAddress"
  | "npAutoMatched"
  | "npDone";

interface Entry {
  screen: OnbScreen;
  supplierId: string;
  phone: string;
  isNew: boolean | null;
  reason?: CylinderInfo["state"];
  /** 掃碼比對到的既有客戶（遮罩資料），供 existingPhone 顯示確認卡 */
  matchedCust?: CylinderCustomer;
  /** 入口來源：flyer 的新客戶走完整註冊流程，qr 走快速註冊 */
  src?: "qr" | "flyer";
  /** 掃 QR·不能掃描：手機 → OTP → 確認客戶資料（入口就驗 OTP） */
  noScan?: boolean;
  /** 入口網址帶的瓦斯行邀請碼（?shopCode=），用於 GET /register/supplier-by-shop-code 查真實瓦斯行。 */
  shopCode?: string;
  /**
   * qr 入口且帶 shopCode：能否掃描要等後端查完 cylinder_scan 才知道（權威來源），
   * 查詢期間先停在 qrGate 過渡畫面，不要先閃現 qrBarcode 或 existingPhone 再跳走。
   */
  awaitingScanGate?: boolean;
  /** awaitingScanGate 查無真實瓦斯行（demo/展示、shopCode 沒解析到）或查詢失敗時使用的 fallback，等同純看 URL `scan` 參數的原行為。 */
  fallbackScreen?: OnbScreen;
  fallbackNoScan?: boolean;
  fallbackMatchedCust?: CylinderCustomer;
  fallbackReason?: CylinderInfo["state"];
  /**
   * OA 入口帳戶檢查（F2）的加帳戶流程（已註冊但這家瓦斯行沒帳戶 → 免走新客戶註冊；比對命中
   * 免填 OTP 直接綁定，見 regGate effect；2026-09-03 拍板：收尾照入口原本流程走，不強制進
   * /kiosk）不再靠 URL 的 `?mode=addAccount`
   * 判定（2026-09-02 使用者拍板拿掉該參數）——改由 regGate effect 在「fetchCustomerContexts
   * 200（此 LINE 已註冊）且本次入口能定出瓦斯行（entry.supplierId 有值；或只帶 entry.shopCode
   * 時先用 POST /register/supplier-by-shop-code 反查出 supplier_id——2026-09-02 使用者再次
   * 拍板：實體鋼瓶 QR 貼紙的實際格式只印 shopCode、不印 supplier，補這條反查才不會漏掉這種
   * 入口）」時，於執行期判定並存進 component state `isAddAccountFlow`（見下方宣告），效果是
   * 實體傳單／鋼瓶 QR／OA rich menu 三種入口都走同一套判斷，不再限定「一律傳單入口」。
   */
}

/**
 * 由網址決定入口：
 *   /onboarding/qr?supplier=&scan={0|1}&code=  → 掃鋼瓶 QR
 *   /onboarding/flyer?supplier=                → 傳單
 *   /onboarding（無參數）                       → 中性提示畫面（請由 QR / 傳單連結進入）
 *
 * src 判斷優先看路徑（為未來各入口各自綁定 LIFF endpoint 鋪路），
 * 路徑判不出來時 fallback 用舊版 `?src=qr|flyer` query，
 * 避免已印出去的舊連結（掃碼貼紙/傳單）失效。
 */
function parseEntry(pathname: string, search: string): Entry {
  const params = new URLSearchParams(search);
  const pathSrc = pathname.endsWith("/qr") ? "qr" : pathname.endsWith("/flyer") ? "flyer" : undefined;
  const querySrc = params.get("src");
  const src = pathSrc ?? (querySrc === "qr" || querySrc === "flyer" ? querySrc : undefined);
  const supplierId = params.get("supplier") || "";
  const scanParam = params.get("scan"); // "0" | "1" | null（預設 1）
  const code = params.get("code") || "";
  const status = params.get("status"); // 測試捷徑：matched|joined|invalid|new（直接指定 barcode 狀態）
  const shopCode = (params.get("shopCode") || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12);
  const base: Entry = { screen: "home", supplierId, phone: "", isNew: null, src: src === "flyer" ? "flyer" : src === "qr" ? "qr" : undefined, shopCode: shopCode || undefined };

  if (src === "qr") {
    // demo fallback 決定（純看 URL 參數，等同修改前的行為）：無真實瓦斯行（無 shopCode／查無/查失敗）時採用。
    let fallback: Entry;
    if (scanParam === "0") {
      fallback = { ...base, screen: "existingPhone", noScan: true }; // 不能掃描 → 手機 → OTP → 確認客戶資料
    } else {
      // 狀態來源：?status 測試捷徑優先，其次 ?code 查表
      const st = (["matched", "joined", "invalid", "new"].includes(status || "") ? status : code ? resolveBarcode(code).state : null) as
        | CylinderInfo["state"]
        | null;
      if (st === "matched") {
        const cust = (code ? resolveBarcode(code).cust : undefined) ?? resolveBarcode("TW-2024-8801").cust;
        fallback = { ...base, screen: "existingPhone", isNew: false, matchedCust: cust };
      } else if (st === "joined" || st === "invalid" || st === "new") {
        fallback = { ...base, screen: "existingPhone", reason: st }; // 已註冊/失效/查無 → 手機閘門帶原因
      } else if (scanParam === "1" || code || status) {
        fallback = { ...base, screen: "qrBarcode" }; // demo 參數明確要求掃描 → 引導掃 barcode（含示範樣本）
      } else {
        // 沒有瓦斯行資訊（無 shopCode，或 shopCode 查無/查失敗）且無 demo 參數：
        // 掃描頁/手機閘門都應綁定「已知的瓦斯行」（開關/比對都以它為準），不知道哪家就不該進。
        // 2026-07-15 拍板：一律改走「無指定瓦斯行」流程（手機 → OTP → 跨行找瓦斯行 → 自動配對），
        // 該流程本來就是為「不知道哪家」設計的。
        fallback = { ...base, screen: "npPhone" };
      }
    }
    if (shopCode) {
      // 有 shopCode → 能否掃描以後端 cylinder_scan 為權威，查詢期間先停在過渡畫面（見 index.tsx 的 shopCode effect）。
      // 不預先帶入 fallback 的 matchedCust/reason/noScan，避免跟「查完才決定」的畫面混淆；查完/查失敗後才套用（見 fallback* 欄位）。
      return {
        ...base,
        screen: "qrGate",
        awaitingScanGate: true,
        fallbackScreen: fallback.screen,
        fallbackNoScan: fallback.noScan,
        fallbackMatchedCust: fallback.matchedCust,
        fallbackReason: fallback.reason,
      };
    }
    return fallback; // demo（無 shopCode）→ 維持純看 URL scan 參數的行為
  }
  if (src === "flyer") {
    // 有指定瓦斯行 → 先看資訊卡(adKnown)；無指定 → 專屬流程（手機→OTP→找瓦斯行）
    return { ...base, screen: supplierId ? "adKnown" : "npPhone" };
  }
  return base; // 無參數 → home
}

const Onboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [entry] = useState<Entry>(() => parseEntry(location.pathname, location.search)); // 只解析一次網址
  // 由 URL 決定當前瓦斯行：預設 demo fallback，若入口帶 shopCode 則另外向後端查真實瓦斯行覆蓋（見下方 effect）。
  const [supplier, setSupplier] = useState<Supplier>(() => {
    // 真實入口（帶 shopCode，或 supplierId 非 demo 表內的真實 id 如 GS_1）→ 以空殼起始，
    // 等 shopCode/barcode 解析出真瓦斯行才填入。**絕不**先顯示 demo fallback（幸福瓦斯行）
    // ——載入期間寧可空白也不能洩漏 mock 資料。demo 展示（DevHome/S001~S003）維持原行為。
    if (entry.shopCode || (entry.supplierId && !SUPPLIERS[entry.supplierId])) {
      return { name: "", phone: "", area: "" };
    }
    return getSupplier(entry.supplierId);
  });

  const {
    setPlateCaptured,
    setNoCylinder,
    setHidePrice,
    setNeedSupplierSelect,
    setOtpVerified,
    setPendingReview,
    setNeedIdentityCheck,
    setContractSigned,
    setNotFound,
    setEntrySource,
    setEntryShopCode,
    entrySupplierId,
    setEntrySupplierId,
    phoneVerifiedTicket,
    setPhoneVerifiedTicket,
    matchedCisId,
    setMatchedCisId,
    entryLandline, // 使用者實際填寫的市話（可多支逗號合併）→ walk-in 的 landline_input
    setEntryLandline,
    entryAddress,
    setEntryAddress,
    setEntryAddressNote,
    setEntryCylinderScan,
    setContactPhone,
    claimedExisting,
    setClaimedExisting,
  } = useKiosk();
  const { lineAccessToken } = useLineAuth();
  const { cusJwtToken, setCusJwtToken } = useCusAuth();
  const authReady = useAuthReady();
  /** GA 漏斗名稱：依入口區分 qr / flyer（沒有 src 的少數情況歸 onboarding）。 */
  const onbFunnel = entry.src === "qr" ? "onboarding_qr" : entry.src === "flyer" ? "onboarding_flyer" : "onboarding";
  /** GA 漏斗：這次 onboarding 走的是「既有客戶認領(walk-in)」還是「新客戶註冊(unbound)」。
   *  在分歧點(bindAndFinish / ensureRegistered)設定，完成時據此標 complete_existing / complete_new。 */
  const onbPathRef = useRef<"new" | "existing" | null>(null);
  const [screen, setScreen] = useState<OnbScreen>(entry.screen);
  /** 已註冊閘門（2026-07-16 拍板：LINE 已有 customer 帳號不可再開 onboarding）：
      checking = 查詢中（過渡畫面）；open = 未註冊，開放流程。已註冊者直接導走，不進本頁。 */
  const [regGate, setRegGate] = useState<"checking" | "open">("checking");
  const regGateCheckedRef = useRef(false); // 只在進頁時查一次：流程中 ensureRegistered 建了帳號不可把人踢出流程
  /**
   * 加帳戶流程（原 URL `?mode=addAccount`，2026-09-02 拍板拿掉該參數）：由 regGate effect 執行期判定——
   * fetchCustomerContexts 200（此 LINE 已註冊）且本次入口能定出瓦斯行（entry.supplierId 有值；
   * 或只帶 entry.shopCode 時先反查出 supplier_id——2026-09-02 使用者再次拍板：鋼瓶 QR 貼紙只印
   * shopCode）→ 視為「已註冊客戶對這家瓦斯行加帳戶」，不踢走、改依 entry-check 結果進場。
   *
   * 同時留一個同名 ref：regGate effect 判定成立當下會「同一行」把 state 與 ref 都設成 true——
   * ref 的寫入是同步、立即生效的 plain JS 賦值，不像 setState 要等下一輪 render 才反映在新的
   * closure 上。flags-reset effect（下方，依 regGate 為 dep）讀這個 ref：即使某次改動讓「设 flag」
   * 與「regGate 轉 open」被 React 18 自動批次進同一次 commit，ref 版本仍保證是當下最新值，
   * 不必依賴「setIsAddAccountFlow 一定比 setRegGate('open') 早一個 commit」這種隱含時序假設。
   * 其餘呼叫點（ensureRegistered / finish / finishExistingPending / bindAndFinish）都是使用者
   * 之後才觸發的既有 useCallback／函式，屆時 state 早已隨前面幾輪 render 更新完畢，直接讀 state
   * 即可，不需要 ref。
   */
  const [isAddAccountFlow, setIsAddAccountFlow] = useState(false);
  const isAddAccountFlowRef = useRef(false);
  /**
   * 「有效入口瓦斯行 id」單一來源（2026-09-02 使用者拍板：實體鋼瓶 QR 貼紙只帶 `shopCode`、
   * 不帶 `supplier`——已註冊客戶掃碼時原本連 entry-check 都不會啟動，因為判定條件只看
   * entry.supplierId，這種貼紙格式下它永遠是空字串）。
   *
   * 初值＝entry.supplierId（傳單/OA rich menu 既有情境，URL 直接帶，行為不變）；只帶 shopCode
   * 時 URL 沒有 supplier，regGate effect 改為先用既有 POST /register/supplier-by-shop-code
   * 反查出 supplier_id，查到後把結果寫進這個 ref——以下都改讀它、不再直接讀 entry.supplierId，
   * 否則會被「這次 URL 本來就沒有的」空字串蓋掉：
   *   1. flags-reset effect 的 setEntrySupplierId 來源；
   *   2. ensureRegistered 呼叫 refreshIdentityContexts 的 preferSupplierId；
   *   3. bindAndFinish 呼叫 refreshIdentityContexts 的 preferSupplierId。
   * 用 ref（非 state）：regGate effect 要在「還沒 setRegGate('open')」前就同步寫入給後面幾個
   * 效果/呼叫點讀到，不能等下一輪 render；讀取端（flags-reset effect／使用者操作後才觸發的
   * useCallback）到那時 ref 已經是最新值，不需要用 state 觸發重 render。
   */
  const entrySupplierIdSourceRef = useRef<string>(entry.supplierId || "");
  /** shopCode 反查瓦斯行資訊的快取：regGate effect（已註冊+只帶 shopCode）反查成功後寫入，
   *  供稍後的 shopCode-resolve effect（regGate 轉 open 才跑）直接使用，省一次重複打同一支
   *  POST /register/supplier-by-shop-code（該 API 冪等、輕量，重打也不會壞，但能省則省）。 */
  const shopCodeLookupCacheRef = useRef<{ shopCode: string; data: SupplierByShopCode } | null>(null);
  /** 已依 entry-check 結果決定「終局畫面」（matched→existingPhone／no_match→npAsk）→ true：
   *  擋掉稍後 shopCode-resolve effect 依 entry.awaitingScanGate＋cylinder_scan 把畫面覆蓋回
   *  qrBarcode/existingPhone（那是給「還沒決定要不要能掃描」的未註冊/need_otp 流程用的邏輯，
   *  已註冊且比對出結果的畫面不該再被蓋掉）。need_otp／一般未註冊流程都不設此 ref，維持原行為。 */
  const scanGateResolvedRef = useRef(false);
  const [phone, setPhone] = useState(entry.phone);
  const [phoneBackTo, setPhoneBackTo] = useState<OnbScreen>("home"); // existingPhone 的返回目標
  const [matchedCust, setMatchedCust] = useState<CylinderCustomer | null>(entry.matchedCust ?? null);
  const [reason, setReason] = useState<CylinderInfo["state"] | undefined>(entry.reason);
  // 掃 QR·不能掃描旗標：初值來自 URL（demo fallback），有 shopCode 時等 cylinder_scan 查完會被 shopCode effect 覆寫。
  const [noScan, setNoScan] = useState<boolean>(entry.noScan ?? false);
  const [npAddrMode, setNpAddrMode] = useState<"match" | "plain">("match"); // 無指定流程的地址頁模式
  /**
   * 地址頁要用哪一種比對（每個進入點強制表態，不從 isFlyerKnown / entrySupplierId 反推）：
   *   supplier = 已知瓦斯行 → match/by-address（單一瓦斯行內找既有客戶）
   *   cross    = 未知瓦斯行 → match/suppliers-by-address（跨行找所屬瓦斯行）
   * 反推過一次教訓（matchSource）：同一個畫面服務多條路時，來源必須是明講的，
   * 否則加一條新路（這次是 QR）就會意外改變既有路的行為。
   */
  const [addrScope, setAddrScope] = useState<"supplier" | "cross">("cross");
  /**
   * 地址關「沒比對到 / 否認命中」時要怎麼收尾。入口分類（entryKind）決定不了這件事——
   * 同樣是 QR·有指定進地址頁，客戶按「我是這間客戶」與按「我第一次叫」的收尾就不同：
   *   existingPending = 自稱既有但比對不到 → 以既有身分下單＋標記待瓦斯行核對
   *   newCustomer     = 自稱第一次叫 → 當新客戶建檔
   * 每個進入點明講，不從 claimedExisting 之類的狀態反推。
   */
  const [addrEnding, setAddrEnding] = useState<"existingPending" | "newCustomer">("newCustomer");
  const [matchedSupplierId, setMatchedSupplierId] = useState<string>(""); // 無指定流程比對到的瓦斯行
  const [supplierMatches, setSupplierMatches] = useState<MatchedSupplier[]>([]); // 手機比對到多家（最近叫桶排序）
  // 無指定：跨行比對到的瓦斯行清單（真後端；手機/家用電話比對回 address_masked，地址比對回 phone_masked）
  const [npSupplierMatches, setNpSupplierMatches] = useState<(MatchedSupplierApi | MatchedSupplierByAddressApi)[]>([]);
  const [npConfirmIdx, setNpConfirmIdx] = useState(0); // npConfirmSupplier 顯示/確認的是 npSupplierMatches 的哪一筆（單筆=0；多筆由 npPickSupplier 選）
  // npConfirmSupplier 是由哪種比對來源進來的 → 決定「不是這家」去哪：
  // phone→npAsk（原始切版）、landline→地址頁、address→推薦附近瓦斯行。不可一律，否則三來源耦合。
  const [npConfirmSource, setNpConfirmSource] = useState<"phone" | "landline" | "address">("phone");
  /**
   * 使用者明確否認過的既有客戶（按「這不是我」／「不是這家」的那幾筆 cis_id）。
   * 隨註冊送後端存進 onboarding_registration.rejected_cis_ids，AW 人工核對時把這些候選標成
   * 「客戶已否認」——避免客服又把客戶綁回他自己說不是的那筆資料。
   * 只累加、不移除：整個註冊流程只送出一次，中途改變心意的情境由使用者重新走流程處理。
   */
  const [rejectedCisIds, setRejectedCisIds] = useState<string[]>([]);
  const rejectCis = (cisId?: string | null) => {
    const id = (cisId || "").trim();
    if (!id) return;
    setRejectedCisIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };
  const [npAutoNoMatch, setNpAutoNoMatch] = useState(false); // npAutoMatched 走「查無」變體：只播配對中雷達 → 等待頁 pending
  const [npDoneVariant, setNpDoneVariant] = useState<NpDoneVariant>("registered"); // 無指定完成頁文案
  const [otpDone, setOtpDone] = useState(false); // 掃 QR·不能掃描 / 傳單有指定：入口是否已驗過 OTP
  const [scanning, setScanning] = useState(false); // 正在查詢 barcode（POST /register/cylinder-by-barcode）
  const [addrMatching, setAddrMatching] = useState(false); // 正在比對地址（POST /register/match/by-address 或 suppliers-by-address）
  const [npLandlineMatching, setNpLandlineMatching] = useState(false); // 正在依序比對家用電話（POST /register/match/by-landline 或 suppliers-by-landline）
  const [scannedBarcode, setScannedBarcode] = useState(""); // 最近一次掃到的 barcode，供免 OTP 綁定（barcode+手機）用
  const [otpForBindRetry, setOtpForBindRetry] = useState(false); // barcode 免 OTP 綁定失敗（手機不符/需 OTP）→ 導去 OTP，驗完後重試綁定
  const [binding, setBinding] = useState(false); // 正在呼叫 POST /register/walk-in 綁定既有客戶（免填直接綁）
  const [bindError, setBindError] = useState(""); // walk-in 綁定失敗訊息
  // 傳單・有指定：目前確認卡是否來自家用電話（市話）比對命中（match/by-landline）。
  // 用於「不是我」reject 時接回地址比對（npAddress），還原「電話→地址」兩段式流程，而非誤跳去問「是否叫過桶」。
  const [matchedViaLandline, setMatchedViaLandline] = useState(false);
  // 這次確認卡命中所依據的「單一」市話 / 地址（landline-only / address-only 比對成功時記錄），
  // 供 bindAndFinish 把它們一併送回後端 verifyClaimedCis 驗證（2026-07-13 修正 CRIT-2：
  // 不可用 entryLandline，那是使用者輸入的所有市話「逗號合併清單」，BE landlineMatches 無法比對合併字串）。
  const [matchedLandline, setMatchedLandline] = useState("");
  const [matchedAddress, setMatchedAddress] = useState("");
  /** 客戶在「這是您嗎」確認卡輸入的門牌號（已正規化）：與上面兩者一樣是 verifyClaimedCis 的驗證輸入。
   *  存 state 而非只靠參數，因為 OTP 補驗迴圈會無參數重入 bindAndFinish（同 matchedCisId 的理由）。 */
  const [confirmedHouseNo, setConfirmedHouseNo] = useState("");
  /**
   * 這張「確認客戶資料」卡（existingPhone）是由哪一種比對命中而來。
   *
   * existingPhone 一張卡服務四種來源（手機 OTP / 市話 / 地址 / 掃鐵牌 barcode），而各來源按
   * 「這不是我」該去的下一關完全不同（見設計圖①②）。過去沒有來源欄位，是用
   * matchedViaLandline 布林 ＋ matchedLandline/matchedAddress 有沒有值去「反推」來源，
   * 導致四條路互相耦合：漏清一個殘留值就會走錯分支，新增一條規則也會誤傷其他三條。
   * 這裡改為每個進入點「強制表態」，onReject/onDone 一律 switch 這個欄位，不再反推。
   */
  const [matchSource, setMatchSource] = useState<"phone" | "landline" | "address" | "barcode" | null>(null);
  const isFlyerKnown = entry.src === "flyer" && !!entry.supplierId; // 傳單・有指定瓦斯行
  /**
   * 入口分類：四條路各自具名，不可用某條路的否定去代表另一條路。
   *
   * 起因：原本用 `!isFlyerKnown && entry.src === "qr"` 表示「QR·無指定」，但 isFlyerKnown 的定義
   * 要求 src==="flyer"，對 QR 恆為 false —— 這個條件實際涵蓋「所有 QR」，害 QR·有指定
   * 在否認市話命中時被當成無指定，直接跳一鍵下單，其後「QR·有指定」的分支成為死碼。
   *
   * QR 是否「有指定」以 entrySupplierId（shopCode 解析結果）為準；
   * 傳單沿用 isFlyerKnown（URL 的 supplier 參數），不動既有行為。
   */
  const entryKind: "qrKnown" | "qrUnknown" | "flyerKnown" | "flyerUnknown" =
    entry.src === "qr"
      ? entrySupplierId
        ? "qrKnown"
        : "qrUnknown"
      : isFlyerKnown
        ? "flyerKnown"
        : "flyerUnknown";
  /**
   * 導航返回堆疊。
   *
   * 原本 11 個 onBack 各自寫死返回目標，每個都假設「只有一種進來的方式」；
   * 但多數畫面有 2～6 個入口（npAsk 5 個、npAddress 6 個），
   * 每接一條新路徑就有一個返回鍵指向使用者沒走過的頁面（實測撞過三次）。
   *
   * 改為 go() 進頁時把「當前畫面」壓入堆疊、goBack() 退回上一個真正走過的畫面：
   * 返回永遠回到來時路，新增路徑不必再為每個畫面想返回目標。
   * 堆疊為空 = 這是流程第一頁 → canGoBack 為 false，呼叫端不給 onBack、返回鍵自動不顯示。
   */
  /**
   * 堆疊每一格要連「畫面當時的比對資料」一起快照。
   *
   * 只記畫面名稱不夠：按「這不是我」時會 clearMatched() 清掉 matchedCust 等資料
   *（刻意為之，避免殘留污染下一關比對或誤認領他人 CIS），返回時畫面雖然對了，
   * 但確認卡因為沒有資料而變成「輸入手機」表單——看起來像沒回到原本那一頁。
   * 連狀態一起還原，返回才真的是「回到剛才看到的那一頁」。
   */
  const snapshotMatch = useCallback(
    () => ({
      cust: matchedCust,
      cisId: matchedCisId,
      landline: matchedLandline,
      address: matchedAddress,
      viaLandline: matchedViaLandline,
      source: matchSource,
      otpDone,
    }),
    [matchedCust, matchedCisId, matchedLandline, matchedAddress, matchedViaLandline, matchSource, otpDone],
  );
  type NavFrame = { screen: OnbScreen; match: ReturnType<typeof snapshotMatch> };
  const [navStack, setNavStack] = useState<NavFrame[]>([]);
  // 用 ref 讀「當前畫面／堆疊」而非在 setState 的 updater 裡做事：
  // updater 必須是純函式，React 18 StrictMode 會刻意呼叫兩次來偵測不純——
  // 先前把 setNavStack 寫在 setScreen 的 updater 內，導致同一次導航推了兩格，
  // 返回一次看起來像沒動；goBack 也因為畫面與資料分屬不同 render 而閃一下舊畫面。
  const screenRef = useRef(screen);
  screenRef.current = screen;
  const navStackRef = useRef<NavFrame[]>([]);
  navStackRef.current = navStack;
  const snapshotRef = useRef(snapshotMatch);
  snapshotRef.current = snapshotMatch;

  const go = useCallback((s: OnbScreen) => {
    if (screenRef.current === s) return; // 導到同一頁不推堆疊，避免返回卡在原地
    setNavStack([...navStackRef.current, { screen: screenRef.current, match: snapshotRef.current() }]);
    setScreen(s);
  }, []);

  /** 退回上一個走過的畫面，並還原該畫面當時的比對資料。
   *  所有 setState 都在同一個事件裡呼叫 → React 批次成單次 render，不會閃一下「資料已清空」的畫面。 */
  const goBack = useCallback(() => {
    const st = navStackRef.current;
    if (st.length === 0) return;
    const prev = st[st.length - 1];
    setNavStack(st.slice(0, -1));
    setScreen(prev.screen);
    setMatchedCust(prev.match.cust);
    setMatchedCisId(prev.match.cisId);
    setMatchedLandline(prev.match.landline);
    setMatchedAddress(prev.match.address);
    setMatchedViaLandline(prev.match.viaLandline);
    setMatchSource(prev.match.source);
    setOtpDone(prev.match.otpDone);
    setBindError(""); // 錯誤訊息不還原：那是「上次送出」的結果，不屬於畫面狀態
  }, []);

  /** 走到不可回頭的落點（完成註冊/過場）→ 清空堆疊，避免退回已完成的流程。 */
  const goFinal = useCallback((s: OnbScreen) => {
    setNavStack([]);
    setScreen(s);
  }, []);
  const canGoBack = navStack.length > 0;

  // QR·掃 barcode 這關先彈「允許定位」（仿系統權限對話框）；允許 → 直接帶入送達地址（供 kiosk/結算用）。
  // 註：「確認送達地址」詳細 sheet 屬於「定位地址」步驟，不在 barcode 頁顯示。
  const { chooseIdentity, setChooseIdentity } = useUser();
  const [locateAsk, setLocateAsk] = useState(false); // 拿不到位置時的說明卡（正常情況不會出現，見 locatePermission）
  const [locateBlocked, setLocateBlocked] = useState(false); // 說明卡按了重試仍被擋 → 改顯示怎麼救
  // 本次流程只跑一次定位。用 ref 不用 state：state 一改就會讓 gate effect 因 deps 變動重跑，
  // 而它的 cleanup 會把「還在 await resolveLocateGate」的那次標記為 cancelled，定位就整個沒了。
  const locateStartedRef = useRef(false);
  const [locateAllowed, setLocateAllowed] = useState(false); // 已允許定位 → 後續填地址頁直接帶入
  const [locating, setLocating] = useState(false); // 定位中（GPS + 反查地址）
  const [locAddrLine, setLocAddrLine] = useState(""); // 定位反查到的地址（不含門牌）
  const [locAddrDoor, setLocAddrDoor] = useState(""); // 定位反查到的門牌號
  const [locLat, setLocLat] = useState<number | undefined>(undefined); // GPS 座標 → 餵地圖 pin
  const [locLng, setLocLng] = useState<number | undefined>(undefined);
  // 已註冊閘門（2026-07-16 拍板）：LINE 已有 customer 帳號 → 不開 onboarding，直接導走
  // （QR 入口 → /kiosk 一鍵下單；傳單/其他 → /home）。判定用 GET /customer/contexts：
  // 200 = 有 customer；401/查無（含 delete-user 後殘留的舊 token）= 未註冊 → 開放流程。
  // 只在進頁時查一次（ref）：流程中 ensureRegistered 建了帳號，不可反過來把人踢出流程。
  useEffect(() => {
    if (!authReady || regGateCheckedRef.current) return;
    regGateCheckedRef.current = true;
    setClaimedExisting(null); // 進入註冊流程重置自稱（避免前次流程殘留）
    if (isPracticeMode() || !cusJwtToken) {
      setRegGate("open"); // 練習沙盒不擋；沒有客戶 token = 未註冊
      return;
    }
    (async () => {
      try {
        await fetchCustomerContexts(cusJwtToken); // 200 = 此 LINE 已有 customer
        // 判定「這次入口有沒有指定瓦斯行」：URL 的 ?supplier= 優先；只帶 shopCode（實體鋼瓶 QR
        // 貼紙的實際格式——2026-09-02 使用者拍板：這種貼紙只印 shopCode、不印 supplier）時，
        // 先用既有 POST /register/supplier-by-shop-code 反查出 supplier_id。查無（404／demo
        // 展示碼）或反查本身失敗 → 視同「這次入口沒有指定瓦斯行」判定不成立，falls through
        // 走既有「已註冊導走」行為（不打 entry-check，不擋）。
        let checkSupplierId = entry.supplierId || "";
        const shopCodeForLookup = entry.shopCode || ""; // 先接成本地變數，await 之後仍是明確的 string
        if (!checkSupplierId && shopCodeForLookup) {
          try {
            const shopRes = await fetchSupplierByShopCode(shopCodeForLookup, lineAccessToken || "");
            const shopData = shopRes?.data?.data;
            if (shopData?.supplier_id) {
              checkSupplierId = shopData.supplier_id;
              // 快取反查結果：稍後 shopCode-resolve effect（regGate 轉 open 後才跑）本來就要
              // 再查一次同一個 shopCode（供 UI 顯示瓦斯行名稱/cylinder_scan）——留著讓它直接用，
              // 省一次重複打同一支 API（見該效果內的判斷）。
              shopCodeLookupCacheRef.current = { shopCode: shopCodeForLookup, data: shopData };
            }
          } catch {
            // 反查失敗 → 視同判定不成立，往下走既有「已註冊導走」
          }
        }
        if (checkSupplierId) {
          // 2026-09-02 拍板：拿掉 URL 的 ?mode=addAccount，改用「此 LINE 已註冊＋本次入口能定出
          // 瓦斯行（URL 直帶 supplier，或由 shopCode 反查出）」自行判定加帳戶流程（不踢走，改依
          // entry-check 結果進場）——效果是 OA rich menu／實體傳單／鋼瓶 QR 三種入口統一判斷。
          // ref 立即同步寫入，供本函式與 flags-reset effect 用；state 供其餘 render 後才觸發的
          // 呼叫點（ensureRegistered / finish 等）讀（見上方宣告處的時序說明）。
          isAddAccountFlowRef.current = true;
          setIsAddAccountFlow(true);
          // 分流前先寫入「有效入口瓦斯行 id」單一來源與 kiosk store：checkSupplierId 這次若是
          // shopCode 反查出來的，entry.supplierId（URL）仍是空字串——flags-reset effect 稍後只認
          // entry.supplierId 會把它蓋回空字串，改讀這個 ref 才不會蓋掉（見 ref 宣告處與該效果）。
          entrySupplierIdSourceRef.current = checkSupplierId;
          setEntrySupplierId(checkSupplierId);
          // 重打一次取最新狀態，不信 URL/sessionStorage 殘留（entry-check 失敗一律 fail-open）。
          try {
            const res = await entryCheck({ accessToken: lineAccessToken || "", supplier_id: checkSupplierId });
            const data = res?.data?.data as EntryCheckData;
            if (data.status === "matched" && data.matched) {
              // 免輸手機/OTP：直接出「這是您嗎」確認卡（同 matchPhoneAndRoute 命中分支的整備）
              setMatchedCisId(data.matched.cis_id);
              setMatchedCust(toMatchedCustomer({ phone: "", address_masked: data.matched.address_masked, addresses: data.matched.addresses }));
              setOtpDone(true); // 卡上不再要求手機/OTP
              setMatchedViaLandline(false);
              setMatchSource("phone");
              setMatchedLandline("");
              setMatchedAddress("");
              // 畫面已是終局，擋掉稍後 shopCode-resolve effect 依 awaitingScanGate 覆蓋回掃碼/手機閘門
              scanGateResolvedRef.current = true;
              setRegGate("open");
              goFinal("existingPhone");
              return;
            }
            if (data.status === "no_match") {
              // 已驗證電話沒比中 → 跳過手機+OTP，直接從「是否叫過桶」接回既有比對流程（市話→地址）
              scanGateResolvedRef.current = true; // 理由同上
              setRegGate("open");
              goFinal("npAsk");
              return;
            }
            if (data.status === "need_otp") {
              // 電話沒驗證過（條碼註冊者）→ 走既有「手機 → OTP → match」流程；screen 維持
              // parseEntry 算好的畫面不動——傳單有指定＝adKnown；QR 只帶 shopCode＝qrGate，
              // 稍後 shopCode-resolve effect 會依 cylinder_scan 決定 qrBarcode/existingPhone，
              // 本身就是等效的「手機→OTP→比對」入口，不需要（也不該）在此另外指定畫面。
              setRegGate("open");
              return;
            }
            // has_account（防禦性：provider 已處理過）→ 照舊導走
          } catch {
            // entry-check 失敗 → fail-open：當 need_otp 走完整流程，不擋
            setRegGate("open");
            return;
          }
        }
        const dest = entry.src === "qr" ? "/kiosk" : "/home";
        // eslint-disable-next-line no-console
        console.log(`[註冊] 此 LINE 已有 customer 帳號 → 不開 onboarding，導向 ${dest}`);
        navigate(dest, { replace: true });
      } catch {
        setRegGate("open"); // 401/查無 customer → 未註冊，開放 onboarding
      }
    })();
    // eslint-disable-next-line
  }, [authReady, cusJwtToken]);

  // 到「掃 barcode / 輸入手機號碼」這關 → 開始取定位：
  // QR = 掃 barcode 頁；傳單·無指定 = npPhone；傳單·有指定 = existingPhone。
  //
  // 預設不先問，直接呼叫 getCurrentPosition 讓系統自己的權限窗出面 —— 使用者只要點一次。
  // 只有 resolveLocateGate() 判定「已知拿不到位置」（先前被拒 / 權限 denied）時才彈我們的
  // 說明卡；那種狀態下系統窗本來就不會跳，所以不會出現兩個窗連著問同一件事。
  useEffect(() => {
    const atLocateGate =
      screen === "qrBarcode" ||
      (entry.src === "flyer" && screen === "npPhone") ||
      (isFlyerKnown && screen === "existingPhone");
    if (!atLocateGate || locateStartedRef.current) return;
    locateStartedRef.current = true; // 先鎖住，避免 gate 內換頁時重跑
    void (async () => {
      const gate = await resolveLocateGate();
      if (gate === "ask") setLocateAsk(true);
      else void applyCurrentLocation();
    })();
    // applyCurrentLocation 每次 render 重建，放進 deps 會讓 gate 重跑；ref 已擋住重入
    // eslint-disable-next-line
  }, [screen, entry.src, isFlyerKnown]);
  /**
   * 允許定位 → 取真 GPS（navigator.geolocation）→ OSM Nominatim 免授權反查地址 → 帶入配送地址頁。
   * 定位/反查失敗、拒絕、或查無 → 退回手動輸入（不 prefill）。
   *
   * 背景執行、不擋畫面：呼叫端按下「允許」當下就關掉詢問卡，使用者繼續掃鋼瓶／填手機，
   * 定位在背後跑。這關到地址頁之間一定隔著一關 OTP（等簡訊），GPS timeout 只有 10 秒，
   * 結果幾乎必定先到；萬一沒到，NpAddress 會用晚到的 prefill 補上（見該頁 late-prefill 同步）。
   * 因為畫面上不再有「定位中…」，改用不自動消失的 toast 讓使用者知道背景在跑
   * ——也順帶替接著跳出來的「系統」定位權限對話框做鋪陳。
   */
  const applyCurrentLocation = async ({ silent = false }: { silent?: boolean } = {}): Promise<boolean> => {
    setLocating(true);
    // 進行中刻意不出聲：現在是系統權限窗先出面，使用者點完「允許」就該能繼續掃鋼瓶。
    // 再疊一顆「正在取得您的位置…」只是在系統窗旁邊多一個講同一件事的東西，
    // 而且成功時它什麼也沒告訴使用者 —— 地址會自己出現在後面的地址頁。
    // silent 只剩一個作用：說明卡自己有按鈕 spinner 與失敗後的 blocked 畫面，不需要下面那顆失敗提示。
    try {
      const loc = await getCurrentLocation();
      if (loc.state !== "success") {
        // 失敗才出聲。背景模式沒有任何畫面元素會反映失敗，不講一聲使用者只會覺得按了沒反應
        if (!silent) appToast.info("無法取得位置，稍後請手動輸入地址");
        return false; // 定位失敗/拒絕 → 手動填
      }
      const lat = Number(loc.lat);
      const lng = Number(loc.lng);
      // GPS 成功即記座標並允許帶入：地圖 pin 一律跟真實 GPS（就算反查地址失敗也是）
      setLocLat(lat);
      setLocLng(lng);
      setLocateAllowed(true);
      const addr = await geocodeToAddress(lat, lng);
      if (addr && (addr.city || addr.road)) {
        const line = combinedAddress({ city: addr.city, area: addr.area, road: addr.road }); // 不含門牌
        const door = addr.streetNumber ? (addr.streetNumber.endsWith("號") ? addr.streetNumber : `${addr.streetNumber}號`) : "";
        const full = combinedAddress({ city: addr.city, area: addr.area, road: addr.road, streetNumber: addr.streetNumber });
        setLocAddrLine(line);
        setLocAddrDoor(door);
        // 把定位地址寫進共用 store，kiosk/結算即有送達地址（onboarding 未登入時 chooseIdentity 可能為 null）
        const gpsAddr: Address = { customer_address_id: "gps", full_address: full, city: addr.city, district: addr.area };
        setChooseIdentity(
          chooseIdentity
            ? { ...chooseIdentity, choose_address: gpsAddr }
            : { context_id: 0, context_name: "gps", role: "owner", is_payer: true, member_count: 1, max_members: 1, choose_address: gpsAddr },
        );
      }
      // 反查失敗但定位成功 → 座標仍可用（地圖 pin），地址欄讓使用者手動填
      return true;
    } finally {
      setLocating(false);
    }
  };

  /**
   * 重置上次殘留的 kiosk 結帳旗標（sessionStorage 舊值會干擾下一次流程／測試）。
   *
   * 只在「確定未註冊、真的要開始 onboarding」（regGate === "open"）後才清 —— 不可一進頁就清：
   * 已註冊的人重開註冊連結時會被閘門導回首頁/一鍵下單，他根本沒進流程，卻會被清掉
   * needIdentityCheck（首頁不再顯示「完成加入設定」）與 otpVerified（結算頁要求重驗 OTP），
   * 兩個都是實際回報過的狀況。
   */
  useEffect(() => {
    if (regGate !== "open") return;
    setPlateCaptured(false);
    setNoCylinder(false);
    setOtpVerified(false);
    setHidePrice(false);
    setNeedSupplierSelect(false);
    setPendingReview(false);
    setNeedIdentityCheck(false);
    setContractSigned(false);
    setEntrySource(entry.src ?? null);
    setEntryShopCode(entry.shopCode || "");
    /**
     * QR 入口先用網址上的 ?supplier= 當底，之後 shopCode 解析成功再以後端結果覆蓋（權威）。
     *
     * 原本一律清成 ""，於是只要 shopCode 查不到／查失敗（後端錯誤、該 shopCode 未建檔、
     * token 時序），entrySupplierId 就永遠是空的 → entryKind 被判成 "qrUnknown"。
     * 而 QR 的每一條「這不是我」收尾都是靠 entryKind 分流的，qrUnknown 會全部掉回傳單式結局：
     * goNpRecommend → 配對動畫 → NpDone「完成加入！」→ 前往主頁，與「QR 一律以一鍵下單收尾」
     * （James 2026-07-20 拍板）相反。實際回報：鋼瓶 QR 網址明明帶了 supplier=GS_1 仍走到完成頁。
     *
     * 貼在鋼瓶上的 QR 兩個參數指的是同一家（supplier 與 shopCode 同時產生），
     * 以 supplier 當備援不會配到別家；傳單維持原本行為（isFlyerKnown 另外看 entry.supplierId）。
     *
     * 加帳戶流程（isAddAccountFlow，2026-09-02 拍板拿掉 ?mode=addAccount 後不再限定「一律傳單
     * 入口」——已註冊＋入口帶 supplier 即成立，QR/傳單/OA rich menu 皆可）：URL 帶的 supplier
     * 就是入口瓦斯行本尊（F2/OA 導頁一併帶 supplier + shopCode），同樣先用它當底，之後 shopCode
     * 解析成功覆蓋。沒有這行，本效果會把 regGate 剛轉 open 時 Step2 entry-check 需要的
     * entrySupplierId 洗回空字串（本效果與 shopCode 解析效果都掛在 regGate==="open"，本效果先跑）。
     *
     * 這裡讀 isAddAccountFlowRef（非 state）：regGate effect 判定成立時是「ref 與
     * setIsAddAccountFlow 同一行寫入」，ref 為同步賦值，本效果只要跑在那之後（必然如此——
     * 本效果的觸發條件 regGate==="open" 正是由那段程式碼稍晚才 setRegGate("open") 造成），
     * ref 保證已是最新值，不需要把 isAddAccountFlow state 放進 deps 陣列。
     *
     * 2026-09-02 補（使用者拍板：鋼瓶 QR 貼紙只帶 shopCode、不帶 supplier）：這種入口
     * entry.supplierId 永遠是空字串，若這裡還是只認它，會把 regGate effect 剛用 shopCode
     * 反查出來、已經 setEntrySupplierId 寫過的值蓋回空字串。改讀 entrySupplierIdSourceRef——
     * 它的初值就是 entry.supplierId（一般傳單/OA rich menu 帶 supplier 的情境不受影響），
     * regGate effect 反查成功時會同步更新該 ref，兩種入口統一由這一個「有效入口瓦斯行 id」
     * 單一來源決定，不必在這裡分別判斷是走哪一種。
     */
    setEntrySupplierId(entry.src === "qr" || isAddAccountFlowRef.current ? entrySupplierIdSourceRef.current || "" : "");
    setPhoneVerifiedTicket("");
    /**
     * 加帳戶流程（isAddAccountFlowRef.current）不清 matchedCisId：Step2 entry-check 的 matched
     * 分流與 setRegGate("open") 同一批次寫入 matchedCisId（真的比中的 cis_id），本效果掛在
     * regGate==="open" 上、緊接著下一輪 render 就會執行——若在這裡無條件洗成 ""，會蓋掉
     * Step2 剛設的值，使用者按「這是我」時 bindAndFinish 的 boundCisId 就讀到空字串，
     * 免OTP直綁的核心驗證欄位（matchedCisId）送到後端變成 undefined（實測踩到的 critical）。
     *
     * no_match / need_otp 兩個分流本來就不寫 matchedCisId，略過清空不會讓「未匹配過的殘留
     * cis_id」流進真正的綁定請求：全檔案 setMatchedCisId 一律與 setMatchedCust／phoneCust
     * 成對寫入（見 matchPhoneAndRoute／ExistingPhone submit／各 np* 比對分支），而
     * bindAndFinish 只在 matchedCust／isMatch 為真時才讀 matchedCisId——matchedCust 是
     * 這次 mount 全新的 local state（不受本效果影響、也不是 sessionStorage 殘留），
     * 故不會有「matchedCust 為空、卻讀到舊 cis_id」的情況。非加帳戶流程行為不變（仍清空）。
     */
    if (!isAddAccountFlowRef.current) setMatchedCisId("");
    setEntryLandline("");
    setEntryAddress("");
    setEntryAddressNote("");
    setEntryCylinderScan(false);
    setNotFound(false);
  }, [
    regGate,
    entry.src,
    entry.shopCode,
    entry.supplierId,
    // isAddAccountFlowRef 是 ref，非 reactive 值：不放進 deps（見上方效果內註解的時序理由）。
    setPlateCaptured,
    setNoCylinder,
    setOtpVerified,
    setHidePrice,
    setNeedSupplierSelect,
    setPendingReview,
    setNeedIdentityCheck,
    setContractSigned,
    setNotFound,
    setEntrySource,
    setEntryShopCode,
    setEntrySupplierId,
    setPhoneVerifiedTicket,
    setMatchedCisId,
    setEntryLandline,
    setEntryAddress,
    setEntryCylinderScan,
  ]);

  // 入口帶 shopCode → 向後端查真實瓦斯行，覆蓋 demo fallback；查無/失敗則維持 fallback，不 crash。
  // qr 入口且 awaitingScanGate：一併用回傳的 cylinder_scan 決定能否掃描（權威來源），查完/查失敗才離開 qrGate 過渡畫面。
  useEffect(() => {
    if (!entry.shopCode) return;
    /**
     * 必須等註冊閘門開了才查（regGate 在 deps）——上面的重置 effect 會 setEntrySupplierId("")，
     * 而它的觸發條件是 regGate 轉 "open"（非同步查「是否已註冊」的結果）。
     *
     * 少了這道 guard 就是競態：本 effect 若比閘門先跑完，解析出來的 supplier_id 會被隨後的重置清成 ""，
     * 而本 effect 的 deps 沒變、不會重跑 → 之後整條流程都以為「沒有瓦斯行」。
     * 實測後果：QR 掃碼進來、shopCode 明明解析成 GS_1，按「我第一次叫這家瓦斯」卻送出
     * supplier_unmatched=true（ensureRegistered 的 unmatched = !entrySupplierId）→ 被寫進等待名單
     * → /customer/contexts 回 waiting_supplier_arrangement=true → 卡在「訂購功能即將開放」。
     */
    if (regGate !== "open") return;
    // 後端 supplier-by-shop-code 已改 POST + LineUserGuard（只從 body 讀 accessToken）。
    // 等 LINE token 就緒才查；token 到位會使本 effect 重跑（lineAccessToken 在 deps）。
    if (!lineAccessToken) return;
    let cancelled = false;

    // 查無真實瓦斯行 / 查詢失敗 → 用 parseEntry 算好的 demo fallback（等同純看 URL scan 參數）
    const applyFallbackScanGate = () => {
      if (!entry.awaitingScanGate) return;
      setNoScan(entry.fallbackNoScan ?? false);
      setMatchedCust(entry.fallbackMatchedCust ?? null);
      setReason(entry.fallbackReason);
      setPhoneBackTo("qrBarcode");
      const fallbackScreen = entry.fallbackScreen ?? "qrBarcode";
      setScreen(fallbackScreen);
    };

    // 套用查到的瓦斯行資料（不論是這次剛查的、還是 regGate effect 反查快取的）。
    const applySupplierData = (data: SupplierByShopCode) => {
      setSupplier({ name: data.supplier_name, phone: data.phone || "", area: data.city || "", address: data.address || "", supplier_id: data.supplier_id });
      setEntrySupplierId(data.supplier_id);
      entrySupplierIdSourceRef.current = data.supplier_id; // 同步「有效入口瓦斯行 id」單一來源（見宣告處）
      setEntryCylinderScan(!!data.cylinder_scan); // 鋼瓶掃描開關：供完成加入設定的鐵牌拍照決定是否嘗試讀碼
      // scanGateResolvedRef：regGate effect 若已用同一個 shopCode 反查、並依 entry-check 結果
      // 決定了終局畫面（已註冊＋matched/no_match，見該效果），這裡就不可再依 cylinder_scan
      // 把畫面覆蓋回 qrBarcode/existingPhone——會把「這是您嗎」確認卡／「是否叫過桶」又蓋掉。
      // need_otp 分流與一般未註冊流程都沒設這個 ref，維持原行為。
      if (entry.awaitingScanGate && !scanGateResolvedRef.current) {
        if (data.cylinder_scan) {
          setScreen("qrBarcode"); // 可掃 → 進 barcode 掃描畫面
        } else {
          // 不可掃 → 直接走「不能掃描」路徑：手機 → OTP → 確認客戶資料
          setNoScan(true);
          setMatchedCust(null);
          setReason(undefined);
          setPhoneBackTo("qrBarcode");
          setScreen("existingPhone");
        }
      }
    };

    // regGate effect（已註冊＋只帶 shopCode）若已經反查過同一個 shopCode，直接用快取結果，
    // 不重打一次 supplier-by-shop-code（該 API 冪等、輕量，重打也不會壞，但能省則省）。
    const cached = shopCodeLookupCacheRef.current;
    if (cached && cached.shopCode === entry.shopCode) {
      applySupplierData(cached.data);
    } else {
      fetchSupplierByShopCode(entry.shopCode, lineAccessToken)
        .then((res) => {
          if (cancelled) return;
          const data = res?.data?.data;
          if (data?.supplier_id) applySupplierData(data);
          else applyFallbackScanGate(); // 查無此 shopCode 對應的瓦斯行（demo/展示）→ fallback
        })
        .catch(() => {
          // 查無此 shopCode 或後端錯誤 → 保留 demo fallback 供展示，不擋流程
          if (cancelled) return;
          applyFallbackScanGate();
        });
    }
    return () => {
      cancelled = true;
    };
  }, [regGate, entry.shopCode, entry.awaitingScanGate, entry.fallbackNoScan, entry.fallbackMatchedCust, entry.fallbackReason, entry.fallbackScreen, setEntrySupplierId, lineAccessToken]);

  /** 完成 → 回 app 首頁 */
  const enterHome = useCallback(() => {
    // GA 漏斗完成點（僅 GA、不進 app tracker）：依走的路徑分 complete_new / complete_existing
    trackGa("funnel_step", { funnel: onbFunnel, step: onbPathRef.current ? `complete_${onbPathRef.current}` : "complete" });
    navigate("/home");
  }, [navigate, onbFunnel]);

  // GA onboarding 漏斗（僅 GA、不進 app tracker）：每進入一個 onboarding screen 送一筆
  // funnel_step（step = screen 名稱）。**追蹤所有使用者**（含既有客戶認領，不再只限新客戶）；
  // 走新客戶註冊 or 既有客戶認領由 register_new / bind_existing 兩個 step 分辨，
  // 完成再分 complete_new / complete_existing。qr / flyer 以 funnel 區分。
  useEffect(() => {
    trackGa("funnel_step", { funnel: onbFunnel, step: screen });
  }, [screen, onbFunnel]);

  /** 無指定流程完成頁：帶入文案情境後顯示；新客戶/未匹配到瓦斯行 → 下單前需選瓦斯行 */
  const goNpDone = (v: NpDoneVariant) => {
    setNpDoneVariant(v);
    setNeedSupplierSelect(v !== "welcome"); // welcome=已找到瓦斯行；registered/pending=尚未綁定
    setNeedIdentityCheck(v !== "welcome"); // welcome=既有客戶(免就地註冊)；其餘=新客戶/待核對→gasShop 結帳補註冊
    go("npDone");
  };

  // 完成註冊過場（2026-07-14 拍板）：**所有**完成註冊的路徑都播「配對中 → 完成頁」動畫
  //（NpAutoMatched 雷達過場 → NpDone），最終落點不變 —— 傳單 → 首頁、QR → 一鍵下單。
  // npDoneNextRef 記住落點，按鈕文字依落點切換；過場顯示「真實」supplier（絕不用 demo 表）。
  // （既有直接 go("npDone") 的無指定路徑不設 ref → 預設 enterHome，行為不變。）
  const npDoneNextRef = useRef<(() => void) | null>(null);
  // 未配對到瓦斯行的原因（寫進等待列 waiting_reason）；goNpRecommend 落空時設定，ensureRegistered 帶給後端
  const unmatchedReasonRef = useRef<"no_location" | "no_supplier_in_area" | null>(null);
  const [npDoneCta, setNpDoneCta] = useState("前往主頁");
  const celebrateThen = (variant: NpDoneVariant, cta: string, next: () => void) => {
    // QR 入口不該播完成頁（一律一鍵下單收尾）。真的走到就把當下的分流依據印出來，
    // 現場才查得出是 entryKind 判錯、還是又多了一條沒依入口分流的收尾。
    if (entry.src === "qr") {
      // eslint-disable-next-line no-console
      console.warn(`[註冊] QR 入口卻走到完成頁：variant=${variant} entryKind=${entryKind} entrySupplierId=${entrySupplierId || "無"} shopCode=${entry.shopCode || "無"} urlSupplier=${entry.supplierId || "無"}`);
    }
    setNpDoneVariant(variant);
    setNpDoneCta(cta);
    npDoneNextRef.current = next;
    // 「正在配對瓦斯行」過場只給「未比對到」的情境（registered/pending＝系統配對/待核對）；
    // 比對到既有客戶（welcome＝歡迎回來）本來就不存在配對 → 直接進完成頁（2026-07-15 拍板）。
    goFinal(variant === "welcome" ? "npDone" : "npAutoMatched");
  };

  /**
   * 家用電話（市話，可多組）→ 依序打真後端逐一比對，第一組比對到即停：
   *   有指定（isFlyerKnown && entrySupplierId）：match/by-landline，命中 → 直接進「確認客戶資料」(existingPhone)。
   *   無指定：match/suppliers-by-landline，命中 → 進選/確認瓦斯行 (npPickSupplier/npConfirmSupplier)。
   * 全部沒中（或沒填家用電話 landlines=[]，或無真實 supplier_id 的 demo/展示模式）→ 改用地址比對 (npAddress)。
   */
  const npMatchLandlineAndRoute = async (landlines: string[]) => {
    if (npLandlineMatching) return; // 避免重複送出
    if (landlines.length) setEntryLandline(landlines.join(","));
    if (landlines.length === 0) {
      setAddrScope(isFlyerKnown ? "supplier" : "cross");
      setNpAddrMode("match");
      go("npAddress");
      return;
    }
    setNpLandlineMatching(true);
    try {
      if (isFlyerKnown) {
        if (entrySupplierId) {
          for (const landline of landlines) {
            try {
              const res = await matchByLandline({ accessToken: lineAccessToken || "", supplier_id: entrySupplierId, landline });
              const data = res?.data?.data;
              // 地址不完整 → 視為這一支沒命中（繼續試下一支／最後走原本的沒命中分流）
              if (data?.matched && data.customer && !data.customer.address_incomplete) {
                if (data.customer.cis_id) setMatchedCisId(data.customer.cis_id); // 供後續 walk-in 綁定（後端仍會重驗）
                setMatchedCust(
                  toMatchedCustomer({
                    phone: data.customer.phone_masked || "",
                    address_masked: data.customer.address_masked,
                    addresses: data.customer.addresses,
                  }),
                );
                setOtpDone(true); // 確認卡只顯示客戶資料
                setMatchedViaLandline(true); // 這張確認卡來自市話比對 → reject 時接回地址比對
                setMatchSource("landline");
                setMatchedLandline(landline); // 記下這支「比對成功」的單一市話，供 bindAndFinish 送後端驗證
                setMatchedAddress("");
                go("existingPhone");
                return;
              }
            } catch {
              // 這組電話查詢失敗（網路/後端錯誤）→ 不擋流程，略過繼續試下一組
            }
          }
        }
        // 全部沒中，或無真實 supplier_id（demo/展示模式，shopCode 未解析）→ 改用地址比對
        // demo（無 supplier_id）打不了單一瓦斯行比對 → 退回跨行比對
        setAddrScope(entrySupplierId ? "supplier" : "cross");
        setNpAddrMode("match");
        go("npAddress");
      } else {
        for (const landline of landlines) {
          try {
            const res = await matchSuppliersByLandline({ accessToken: lineAccessToken || "", landline });
            // 地址不完整的候選剔除（同 suppliers-by-phone 的理由）
            const list = (res?.data?.data?.suppliers ?? []).filter((x) => !x.address_incomplete);
            if (list.length > 0) {
              setNpSupplierMatches(list);
              // 記下這支「比對成功」的單一市話：稍後在 npConfirmSupplier「是這家」onYes 送 bindAndFinish
              // 時要用它讓後端 verifyClaimedCis 驗證（跨行清單命中同樣非手機 OTP 精確比對）。
              setMatchedLandline(landline);
              setMatchedAddress("");
              setNpConfirmSource("landline");
              setNpConfirmIdx(0);
              go(list.length > 1 ? "npPickSupplier" : "npConfirmSupplier");
              return;
            }
          } catch {
            // 這組電話查詢失敗（網路/後端錯誤）→ 不擋流程，略過繼續試下一組
          }
        }
        setAddrScope("cross");
        setNpAddrMode("match");
        go("npAddress");
      }
    } finally {
      setNpLandlineMatching(false);
    }
  };

  /**
   * 無指定：逐層比對全落空 → 依客戶座標推薦附近合作瓦斯行（真後端，取代舊 demo 假配對 S001）。
   * 座標來源：GPS（已允許定位）優先，否則以填的地址 geocode；都拿不到就無座標查詢（回未排序清單）。
   * 有清單 → 自動取「最推薦的第一筆」直接配對，播「正在為您配對瓦斯行 → 已為您配對合作瓦斯行」
   * 過場（npAutoMatched），不再出清單讓客戶自選（2026-07-15 拍板）；查無/失敗 → 誠實收尾：
   * 待核對完成頁（不假裝配對成功）。
   */
  const goNpRecommend = async (addr?: string, mapLat?: number, mapLng?: number) => {
    let lat: number | undefined;
    let lng: number | undefined;
    // 縣市門檻：從本次流程的地址解析客戶縣市（台/臺皆可）帶給後端，
    // 只推薦「本縣市＋鄰近縣市」的瓦斯行（例：屏東客戶可配高雄行，跨半個臺灣不配）。
    const TW_CITY_RE = /(臺北市|台北市|新北市|基隆市|桃園市|新竹市|新竹縣|苗栗縣|臺中市|台中市|彰化縣|南投縣|雲林縣|嘉義市|嘉義縣|臺南市|台南市|高雄市|屏東縣|宜蘭縣|花蓮縣|臺東縣|台東縣|澎湖縣|金門縣|連江縣)/;
    const city = (addr || entryAddress || "").match(TW_CITY_RE)?.[1];
    // 座標優先序：①地址頁地圖（客戶自己確認/微調過的配送定位，最可信）
    //             ②本次流程的 GPS 定位 ③地址 geocode（OpenStreetMap 對台灣詳細地址命中率低）
    // 缺座標會讓後端算不出距離、前端放棄配對而把客戶誤丟進「等待安排瓦斯行」，故務必優先用地圖那組。
    if (typeof mapLat === "number" && typeof mapLng === "number" && Number.isFinite(mapLat) && Number.isFinite(mapLng)) {
      lat = mapLat;
      lng = mapLng;
    } else if (locateAllowed && typeof locLat === "number" && typeof locLng === "number") {
      lat = locLat;
      lng = locLng;
    } else if (addr || entryAddress) {
      try {
        const g = await addressToGeocode((addr || entryAddress) as string);
        if (g.state === "success") {
          lat = Number(g.lat);
          lng = Number(g.lng);
        }
      } catch {
        // geocode 失敗 → 無座標查詢
      }
    }
    try {
      // 一併送出地址：後端會以它 geocode 取「權威座標」，不採信這裡的 lat/lng
      // （地圖 pin 可能是上一次 GPS 或預設值殘留，改了地址未必跟著移動 → 會配到百公里外）。
      // lat/lng 仍送，作為後端 geocode 失敗時的備援。
      const res = await recommendSuppliers({ accessToken: lineAccessToken || "", lat, lng, city, address: addr || entryAddress || undefined });
      const list = res?.data?.data?.suppliers ?? [];
      // eslint-disable-next-line no-console
      console.log(`[註冊] 推薦瓦斯行(match/recommend-suppliers) lat=${lat ?? "無"} lng=${lng ?? "無"} city=${city ?? "無"} → ${list.length} 家${list[0] ? `（最近 ${list[0].supplier_name}${list[0].distance_km != null ? ` ${list[0].distance_km}km` : ""}）` : ""}`);
      // 只有「真的有排序依據」（distance_km 有值 = 後端有座標可算距離）才自動配對。
      // 無座標時後端回未排序清單，「第一筆」只是 DB 順序 —— 拿來當「最推薦」會把客戶
      // 綁到隨機瓦斯行（code review 2026-07-15）→ 改走誠實等待收尾。
      if (list.length > 0 && list[0].distance_km != null) {
        const s = list[0]; // 依距離排序的第一筆 = 最推薦
        // eslint-disable-next-line no-console
        console.log(`[註冊] 推薦瓦斯行自動配對（取最推薦第一筆）→ ${s.supplier_id}（${s.supplier_name} ${s.distance_km}km）`);
        setEntrySupplierId(s.supplier_id); // 之後完成加入設定/下單以此行為目標
        setSupplier({ name: s.supplier_name, phone: "", area: s.area || "", supplier_id: s.supplier_id });
        go("npAutoMatched"); // 「正在為您配對瓦斯行」→「已為您配對合作瓦斯行」過場 → confirmAutoMatched → npDone
        return;
      }
      if (list.length > 0) {
        // eslint-disable-next-line no-console
        console.log("[註冊] 推薦清單無距離資訊（無座標）→ 不自動配對，改走等待收尾");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log("[註冊] 推薦瓦斯行查詢失敗:", err);
    }
    // 查無合作瓦斯行/查詢失敗/無排序依據 → 誠實收尾（等待名單），不假裝配對成功。
    // 未配對原因記進等待列（James 2026-07-15 拍板：沒開定位導致配不到要特別記錄）：
    //   no_location = 沒座標（GPS 未開/失敗且地址 geocode 失敗）→ 根本無從配對
    //   no_supplier_in_area = 有座標，但縣市門檻範圍內查無合作瓦斯行
    unmatchedReasonRef.current = typeof lat === "number" && typeof lng === "number" ? "no_supplier_in_area" : "no_location";
    // 註冊＋等待名單要在「進頁面當下」就完成，不能等使用者按「前往主頁」——
    // 客戶看完「會通知您」很可能直接關掉 LINE，不先建就從此消失，平台不會知道他在等。
    // 失敗重試一次；仍失敗 → 誠實提醒（頁面文案會說「資料已登記」，沒登記成功不可裝沒事），
    // npDone CTA 的 ensureRegistered 是最後備援。
    let registered = await ensureRegistered();
    if (!registered) registered = await ensureRegistered();
    if (!registered) appToast.error("網路不穩，資料登記未完成，請按「前往主頁」重試");
    setNeedSupplierSelect(false);
    setNeedIdentityCheck(true);
    setNpDoneVariant("pending");
    // 查無也要有「配對中」過場（與有配對到一致、不突兀）：先播雷達 → onDone 進等待頁 pending。
    setNpAutoNoMatch(true);
    go("npAutoMatched");
  };

  /** 過場結束 → 當新客戶完成加入（首次設定）。 */
  const confirmAutoMatched = () => {
    setNeedSupplierSelect(false);
    setNeedIdentityCheck(true);
    // QR 入口一律以一鍵下單收尾、不播「完成加入！」完成頁（James 2026-07-20 拍板）。
    // 能走到這裡代表 goNpRecommend 剛自動配對成功並 setEntrySupplierId，kiosk bootstrap 有對象；
    // 查無瓦斯行的那條走 pending 等待頁（npAutoNoMatch），不會進來 —— 那條沒有 supplier，
    // 進 kiosk 會壞，維持導首頁才是對的。
    if (entry.src === "qr") {
      void finish(true, phone);
      return;
    }
    setNpDoneVariant("registered");
    go("npDone");
  };

  /**
   * 階段一（401 fix）：註冊「走完就建出 customer」，不再拖到首頁的「完成加入設定」。
   * 任何「新客戶」路徑進首頁前呼叫此函式，確保這個 LINE 帳號已有 customer
   * （linebot_user + customer + binding，先不綁任何瓦斯行 CIS）→ /line/login 拿得到 customer、
   * 首頁不再 401。CIS 之後由「下單時 find-or-create 未確認佔位 CIS」補（見設計：CIS 延到下單）。
   * 後端 unbound-customer 冪等（已註冊回 alreadyRegistered），故命中綁定路徑重複呼叫也安全。
   * 回傳是否已確保註冊（false = 無 OTP 票證等，無法建；呼叫端可據此決定是否提示）。
   */
  /**
   * 註冊/綁定成功後刷新身分：以「新 jwt」重拉 contexts、重設 chooseIdentity。
   * 不刷新會殘留舊客戶的 context（重註冊/換人後），之後所有帶 X-Context 的 API 都 403。
   * pickChooseIdentity：舊 context 仍存在 → 沿用（保地址）；不存在（換了客戶）→ 落到新預設。
   *
   * preferSupplierId（加帳戶模式用）：這次是幫已有帳戶的客戶「多加一家瓦斯行」，剛綁定/建立的
   * 那筆 context 沒有回傳 context_id 可比對，只能重拉 contexts 後找「primary_supplier 等於本次
   * 入口瓦斯行」的那一筆切過去——否則 pickChooseIdentity 會沿用舊 currentChooseIdentity（別家瓦斯行），
   * 使用者綁完新帳戶卻還停在原本的瓦斯行身分上。
   */
  const refreshIdentityContexts = useCallback(
    async (jwt: string, preferSupplierId?: string) => {
      try {
        const res = await fetchCustomerContexts(jwt);
        const data = res.data?.data;
        const contexts = (data?.contexts ?? []) as IdentityContext[];
        const next = pickChooseIdentity(contexts, {
          currentChooseIdentity: chooseIdentity,
          defaultContextId: data?.default_context_id,
        });
        const preferred = preferSupplierId
          ? contexts.find((c) => c.primary_supplier?.supplier_id === preferSupplierId)
          : undefined;
        if (preferred || next) setChooseIdentity(preferred ?? next!);
        // 結帳聯絡電話是持久 store（sessionStorage），換了身分不清會殘留上一個人的號碼
        // → 結帳 OTP 發到錯的手機（真簡訊）。清空讓結帳頁重新以「當前身分」的手機預帶。
        setContactPhone("");
        // eslint-disable-next-line no-console
        console.log(`[註冊] 身分已刷新 → context_id=${(preferred ?? next)?.context_id ?? "無"}（共 ${contexts.length} 個身分）；結帳聯絡電話已重置`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log("[註冊] 身分刷新失敗（將沿用現有身分，可能導致 X-Context 403）:", err);
      }
    },
    [chooseIdentity, setChooseIdentity, setContactPhone],
  );

  const ensureRegistered = useCallback(async (): Promise<boolean> => {
    const ticket = phoneVerifiedTicket || "";
    const digits = (phone || "").replace(/\D/g, "");
    const hasVerifiedPhone = !!(ticket && /^09\d{8}$/.test(digits));
    // 加帳戶流程（已註冊客戶）：這關本來就不會收集/驗證手機（不進 OTP 頁），不能因缺
    // ticket/phone 就跳過——否則佔位 CIS 建不出來，refreshIdentityContexts 找不到入口瓦斯行
    // 的 context，kiosk 落在舊瓦斯行身分（C1）。後端 unbound-customer 已支援無 ticket＋
    // 已註冊 → 用已驗證 main_phone 判定（路徑 C），phone/ticket 兩欄改成有值才帶。
    // 讀 isAddAccountFlow state（非 ref）：ensureRegistered 是使用者操作後才觸發的 useCallback，
    // 呼叫時 regGate effect 早已跑完數輪 render，state 已是最新值。
    if (!hasVerifiedPhone && !isAddAccountFlow) {
      // eslint-disable-next-line no-console
      console.log(`[註冊] ensureRegistered 跳過（無 OTP 票證或手機格式不符）phone=${digits}`);
      return false;
    }
    try {
      // 等待名單標記：走到註冊收尾仍沒有任何目標瓦斯行（比對落空、附近推薦也查無）
      // → 後端寫 onboarding_registration 等待列（supplier NULL），供平台端 waiting list 安排瓦斯行。
      const unmatched = !entrySupplierId;
      // 地址/自稱即時從 store 讀（不用 closure）：NpAddress onDone 會 setEntryAddress(addr) 後
      // 「同一個 event handler 內」立刻呼叫 ensureRegistered，此時 closure 的 entryAddress 還是
      // 上一次 render 的舊值（空）→ 送出註冊帶空地址。zustand set 對 store 是同步的，getState()
      // 取得的是剛寫入的最新值，故送出當下地址就到位（不必等「前往主頁」再補）。
      const kiosk = useKiosk.getState();
      const res = await registerUnboundCustomer({
        accessToken: lineAccessToken || "",
        phone: hasVerifiedPhone ? digits : undefined,
        phone_verified_ticket: hasVerifiedPhone ? ticket : undefined,
        entry_source: entry.src === "qr" ? "qr" : "flyer",
        supplier_unmatched: unmatched,
        // 未配對原因（no_location = 沒開定位且地址解析不出；no_supplier_in_area = 範圍內查無）
        unmatched_reason: unmatched ? unmatchedReasonRef.current ?? "no_supplier_in_area" : undefined,
        // 有入口瓦斯行 → 後端進首頁即建佔位 CIS＋pending_review registration（AW 立即可見）。
        // 帶 onboarding 收集的地址與身分頁按鈕的自稱，佔位 registration 一次到位。
        supplier_id: kiosk.entrySupplierId || undefined,
        // 客戶填過的市話：只要流程走過「填家用電話」就一併記錄（自稱既有·待核對／等待安排瓦斯行
        // 都走這條 unbound-customer 建佔位 registration，AW 人工核對同樣需要看到）。
        landline: kiosk.entryLandline || undefined,
        address: kiosk.entryAddress || undefined,
        claimed_existing:
          typeof kiosk.claimedExisting === "boolean" ? kiosk.claimedExisting : undefined,
        // 使用者明確否認過的既有客戶 → AW 核對時標「客戶已否認」，避免綁回他說不是的那筆
        rejected_cis_ids: rejectedCisIds.length ? rejectedCisIds : undefined,
      });
      const d = res.data?.data;
      const outcomeText = d?.outcome === "bound_to_existing"
        ? "main_phone 撞號 → 綁回『既有』customer（可能是先前測試殘留！）"
        : d?.outcome === "already_registered"
          ? "此 LINE 已註冊（冪等）"
          : "全新建立 customer（無 CIS）";
      // eslint-disable-next-line no-console
      console.log(`[註冊] ensureRegistered → customer_id=${d?.customer_id}｜${outcomeText}${unmatched ? "｜未配對到瓦斯行 → 已記等待名單" : ""}`);
      const jwt = await refreshCustomerSession(lineAccessToken || "");
      if (jwt) {
        setCusJwtToken(jwt);
        // 清掉舊客戶殘留的 chooseIdentity，避免 X-Context 403；加帳戶流程另外偏好切到本次入口瓦斯行的新身分。
        // preferSupplierId 讀 entrySupplierIdSourceRef（「有效入口瓦斯行 id」單一來源，見宣告處）
        // 而非直接讀 entry.supplierId：只帶 shopCode 入口時 entry.supplierId 是空字串，真正的
        // 入口瓦斯行 id 是 regGate effect 反查出來、寫進該 ref 的值。
        await refreshIdentityContexts(jwt, isAddAccountFlow ? entrySupplierIdSourceRef.current : undefined);
      }
      // GA 漏斗：這次走「新客戶註冊(unbound)」路徑（若稍早已標既有認領則不覆蓋）
      if (onbPathRef.current === null) {
        onbPathRef.current = "new";
        trackGa("funnel_step", { funnel: onbFunnel, step: "register_new" });
      }
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log("[註冊] ensureRegistered 失敗:", err);
      return false;
    }
  }, [phoneVerifiedTicket, phone, lineAccessToken, entrySupplierId, entryAddress, claimedExisting, entry.src, isAddAccountFlow, entry.supplierId, setCusJwtToken, refreshIdentityContexts]);

  /** 傳單「有指定瓦斯行」的既有客戶逐層都比對不到 → 到首頁，標記 notFound（首頁顯示「沒有找到您」卡，
      加該瓦斯行 LINE 人工核對）。只用於傳單有指定；傳單無指定一律自動配對瓦斯行，不會走到這裡。
      仍當新客戶（needIdentityCheck）：客戶可加 LINE 等核對，或直接以新客戶做首次設定。 */
  /** QR·有指定地址關的收尾：一律進一鍵下單，只差自稱是新客戶還是既有待核對。 */
  const finishQrAfterAddress = (ph: string) => {
    if (addrEnding === "existingPending") void finishExistingPending(ph);
    else void finish(true, ph);
  };

  const goNotFoundHome = async () => {
    clearShopInviteCode(); // onboarding 收尾一律清入口邀請碼殘留（spec §5.1；原本只有序號註冊完成會清）
    setNotFound(true);
    setMatchedSupplierId("S001"); // demo：先自動匹配最近瓦斯行
    setNeedSupplierSelect(false);
    setNeedIdentityCheck(true);
    await ensureRegistered(); // 進首頁前先建 customer（階段一 401 fix）；失敗不擋流程，仍進首頁
    celebrateThen("registered", "前往主頁", enterHome); // 完成註冊過場 → 首頁（仍帶「沒有找到您」卡）
  };

  /** 進入既有 /kiosk 一鍵下單（帶入手機/新舊客戶提示）。
      真實入口不注入 demo chooseIdentity（2026-07-15 去 demo）：kiosk 商品由 bootstrap 的
      supplier 預覽（entrySupplierId）供應，身分由 ensureRegistered/refreshIdentityContexts 建立。
      練習模式（教學沙盒）例外：假資料攔截層需要 chooseIdentity 帶練習瓦斯行/地址，
      kiosk bootstrap 的 available_suppliers 才對得上 → 維持注入（僅練習）。 */
  const enterKiosk = useCallback(
    (isNew: boolean | null, ph: string) => {
      setHidePrice(false); // 一般客戶顯示價格
      if (isPracticeMode()) {
        const cur = useUser.getState().chooseIdentity;
        const base = cur || {
          context_id: 0,
          context_name: "練習",
          role: "owner",
          is_payer: true,
          member_count: 1,
          max_members: 1,
          choose_address: { customer_address_id: PRACTICE_ADDRESS_ID, full_address: PRACTICE_ADDRESS_FULL },
        };
        setChooseIdentity({ ...base, primary_supplier: buildPrimarySupplier(matchedSupplierId || entry.supplierId) } as never);
      }
      const q = new URLSearchParams();
      const digits = (ph || "").replace(/\D/g, "");
      if (digits) q.set("phone", digits);
      if (isNew !== null) q.set("new", isNew ? "1" : "0");
      // 掃 QR 直接進一鍵下單 → 標記本次為 QR 入口，kiosk 據此在型錄標出「上次訂購」的瓦斯。
      // 用 URL 參數而非 useKiosk.entrySource：entrySource 存在 sessionStorage 且永不清除，
      // 同一分頁走完 QR 後再從首頁進 kiosk 也會殘留成 "qr"，導致一般 kiosk 誤顯示標示。
      if (entry.src === "qr") q.set("src", "qr");
      const qs = q.toString();
      // GA 漏斗完成點（僅 GA、不進 app tracker）：qr 完成 → 進一鍵下單；依路徑分 complete_new / complete_existing
      trackGa("funnel_step", { funnel: onbFunnel, step: onbPathRef.current ? `complete_${onbPathRef.current}` : "complete" });
      navigate(`/kiosk${qs ? `?${qs}` : ""}`);
    },
    // eslint-disable-next-line
    [navigate, setHidePrice, setChooseIdentity, matchedSupplierId, entry.supplierId]
  );

  /** 共用結尾的落點：傳單 → app 首頁；掃 QR → 一鍵下單（皆已知瓦斯行，不需再選） */
  const finish = useCallback(
    async (isNew: boolean | null, ph: string) => {
      clearShopInviteCode(); // onboarding 收尾一律清入口邀請碼殘留（spec §5.1；原本只有序號註冊完成會清）
      setNeedSupplierSelect(false);
      setPendingReview(false); // 已比對到 / 明確新客戶 → 不是待核對
      setNeedIdentityCheck(isNew === true); // 明確新客戶 → 結帳頁再比對；已比對到既有 → 不需要
      if (isNew === true) await ensureRegistered(); // 新客戶進首頁/kiosk 前先建 customer（階段一 401 fix）
      // 結帳聯絡電話優先帶「本次流程用的電話」（身分刷新會清空，這裡回填）
      {
        const d = (ph || "").replace(/\D/g, "");
        if (/^09\d{8}$/.test(d)) setContactPhone(d);
      }
      // 完成註冊過場只給傳單（2026-07-15 拍板 B：QR 維持 dev 原樣，不播完成頁、直接一鍵下單）；
      // 2026-09-03 拍板：加帳戶流程只差第一段比對（免輸手機/OTP 直出確認卡），收尾一律照入口原本流程走
      // （傳單播過場回首頁、QR 進 kiosk），不再因加帳戶而強制改走 /kiosk。
      if (entry.src === "flyer") celebrateThen(isNew === true ? "registered" : "welcome", "前往主頁", enterHome);
      else enterKiosk(isNew, ph);
    },
    // celebrateThen 依賴 state setter（穩定）與 go；不列依賴以避免重建，行為等同原 enterHome/enterKiosk 直呼
    [entry.src, enterHome, enterKiosk, ensureRegistered, setNeedSupplierSelect, setPendingReview, setNeedIdentityCheck, setContactPhone]
  );

  /** 自述是既有客戶但未比對到（忘記/未填登記電話）→ 以既有身分下單，標記待瓦斯行核對（非新客戶）。
      needIdentityCheck + pendingReview 皆 true：結帳頁會做身分核對卡（assumeMatched=pendingReview）；
      傳單首頁的全屏「完成加入設定」只在「新客戶（needIdentityCheck && !pendingReview）」才開，故自述既有不會跳全屏。 */
  const finishExistingPending = useCallback(
    async (ph: string) => {
      clearShopInviteCode(); // onboarding 收尾一律清入口邀請碼殘留（spec §5.1；原本只有序號註冊完成會清）
      setNeedSupplierSelect(false);
      setPendingReview(true);
      setNeedIdentityCheck(true); // 需核對，但因 pendingReview=true 傳單不跳全屏；核對於結帳頁做
      await ensureRegistered(); // 自述既有但沒比對到 → 仍先建 customer（階段一 401 fix），核對留待 AW
      // 結帳聯絡電話優先帶「本次流程用的電話」
      {
        const d = (ph || "").replace(/\D/g, "");
        if (/^09\d{8}$/.test(d)) setContactPhone(d);
      }
      // 完成註冊過場只給傳單（2026-07-15 拍板 B：QR 不播完成頁、直接一鍵下單）
      // 完成頁文案依「有沒有瓦斯行」決定，不可一律 pending：
      //   pending 的內容是「您附近目前還沒有合作瓦斯行，平台安排好會通知您」——那是【無指定且查無】才成立。
      //   有指定瓦斯行（傳單帶 supplier / shopCode 解析成功）時客戶其實已有瓦斯行，只是「自稱既有但比對不到」
      //   要等瓦斯行人工核對，此時顯示「還沒有合作瓦斯行」會誤導（他會以為自己沒被受理）。
      // 2026-09-03 拍板：收尾照入口原本流程，理由同 finish；加帳戶不再強制改走 /kiosk。
      if (entry.src === "flyer") {
        celebrateThen(entrySupplierId ? "registered" : "pending", "前往主頁", enterHome);
      } else enterKiosk(false, ph); // isNew=false：既有身分，不降級為新客戶
    },
    [entry.src, entrySupplierId, enterHome, enterKiosk, ensureRegistered, setNeedSupplierSelect, setPendingReview, setNeedIdentityCheck, setContactPhone]
  );

  /**
   * 「這是我」命中既有客戶（不論命中來源：barcode / 手機比對 / 市話+地址比對）
   * → 免填直接呼叫 POST /register/walk-in 綁定（不進 FirstJoinSetup 填表/簽約），成功即完成（finish）。
   *
   * 驗證方式二擇一：已有 phoneVerifiedTicket（OTP 驗證過）優先使用；否則若這次是掃 barcode 命中，
   * 帶 barcode + 手機讓後端做「barcode 最近持有客戶手機 == 輸入手機」免 OTP 比對（§6 a）。
   * 兩者都沒有（尚未驗證過）→ 導去 OTP 頁拿票證，驗完自動重試一次。
   * barcode 免 OTP 綁定失敗（手機不符）→ 同樣退回 OTP 流程重試。
   */
  // override：呼叫端在「同一個事件」裡剛 setEntrySupplierId/setMatchedCisId 時，closure 讀到的
  // 是舊值（kiosk zustand 的 setter 對元件 closure 非同步）→ 直接把新選定的 supplier/cis 帶進來，
  // 避免誤走 demo 分支。既有呼叫端（existingPhone 換一個 render 才呼叫，state 已 commit）不帶 override。
  const bindAndFinish = async (ph: string, override?: { supplierId?: string; cisId?: string; houseNo?: string }) => {
    const boundSupplierId = override?.supplierId ?? entrySupplierId;
    const boundCisId = override?.cisId ?? matchedCisId;
    // 確認卡剛按下「這是我」的同一個事件裡 state 尚未 commit → 用 override；
    // OTP 補驗迴圈無參數重入時靠 state。
    const boundHouseNo = override?.houseNo ?? confirmedHouseNo;
    const digits = (ph || "").replace(/\D/g, "");
    // 加帳戶流程（已註冊）：電話由後端用註冊時已驗證的 main_phone（walk-in 路徑 C）判定，
    // 前端這關不需要（也拿不到）客戶輸入的手機、更不需要 OTP 票證/barcode；一般流程維持原驗證要求。
    // bindAndFinish 非 useCallback，每次 render 重新定義，讀 state 保證是當下最新值。
    const isAddAccount = isAddAccountFlow;
    // 沒有真實瓦斯行 context（demo/展示模式，shopCode 未解析）→ 不打真後端，維持原行為直接完成
    if (!boundSupplierId && !entry.shopCode) { finish(false, digits || ph); return; }

    const ticket = phoneVerifiedTicket || "";
    const barcode = scannedBarcode || "";
    if (!isAddAccount) {
      if (!/^09\d{8}$/.test(digits)) {
        // 真實入口：手機不合法不可靜默完成（會跳過綁定與註冊 → 沒有 customer，進 kiosk 全 401）→ 停留原頁提示
        const msg = "請輸入本人手機號碼（09 開頭共 10 碼）";
        setBindError(msg);
        appToast.error(msg);
        return;
      }
      if (!ticket && !barcode) {
        // 兩種驗證方式都還沒有 → 先走 OTP 拿票證，驗證完再回來重試一次
        setPhone(digits);
        setOtpForBindRetry(true);
        go("qrOtp");
        return;
      }
    }

    setBindError("");
    setBinding(true);
    try {
      const res = await registerWalkIn({
        accessToken: lineAccessToken || "",
        phoneVerifiedTicket: ticket || undefined,
        barcode: !ticket && barcode ? barcode : undefined, // 有 ticket 優先用 ticket；否則用 barcode 免 OTP
        supplierId: boundSupplierId || undefined,
        shopCode: boundSupplierId ? undefined : entry.shopCode,
        matchedCisId: boundCisId || undefined, // 使用者在確認卡按「這是我」確認的那一筆，後端會驗證此筆是否比對得上
        isNew: false, // 「這是我」= 綁定既有客戶，非新客戶，後端才會驗證/使用 matchedCisId
        claimedExisting: true, // 「這是我」＝自稱既有（綁定成功後端亦強制 true）
        entrySource: entry.src,
        customer: {
          // 加帳戶模式：若 need_otp 子流程已驗過 OTP 拿到 ticket，代表這支號碼「已驗證」，
          // 要帶給後端走路徑 A（有 phone+ticket 才驗證比對），否則白白把剛驗過的號碼丟掉、
          // 落到路徑 C 用舊 main_phone 判定（I1 修法）。真的沒有 ticket（一般加帳戶收尾）
          // 才維持原本不帶，讓後端走路徑 C（用註冊時已驗證的 main_phone）。
          phone: isAddAccount && !ticket ? undefined : digits,
          // 命中來源若是市話/地址比對（非手機 OTP 精確比對），後端 verifyClaimedCis 需要這兩者其一
          // 才驗得過（2026-07-13 修正 CRIT-2）；手機/barcode 命中時兩者皆空，後端走 phoneOk 分支。
          landline: matchedLandline || undefined,
          // 客戶實際填寫的市話：一律回報供 AW 核對（存 registration，不參與驗證）。
          // 不可塞進上面的 landline——那是驗證用的單一號碼，帶了沒比中的號碼會讓後端
          // verifyClaimedCis 判定 landlineOk=false，明明手機已驗證卻綁不了。
          landline_input: entryLandline || undefined,
          // 驗證用地址：市話/地址比對命中時帶完整地址；手機/barcode 命中時沒有完整地址可帶，
          // 只帶客戶在確認卡輸入的門牌號（full_address 為空字串，後端只看 house_no）。
          address: matchedAddress || boundHouseNo ? { full_address: matchedAddress, house_no: boundHouseNo || undefined } : undefined,
        },
        // 注意：這是 request 外層欄位（不在 customer 內），與後端 WalkInDto 一致
        rejected_cis_ids: rejectedCisIds.length ? rejectedCisIds : undefined,
      });
      const data = res.data?.data;
      // GA 漏斗：這次走「既有客戶認領(walk-in)」路徑
      onbPathRef.current = "existing";
      trackGa("funnel_step", { funnel: onbFunnel, step: "bind_existing" });
      // eslint-disable-next-line no-console
      console.log(`[註冊] bindAndFinish 綁定既有客戶成功 → cis_id=${data?.cis_id}｜customer_id=${data?.customer_id}｜review_status=${data?.review_status}（宣稱 matchedCisId=${boundCisId || "無"}）`);
      if (data?.cis_id) setMatchedCisId(data.cis_id); // 換成後端權威 cis_id
      // 綁定成功 → 重新登入刷新客戶身分，首頁才抓得到資料（免整頁重載）
      const jwt = await refreshCustomerSession(lineAccessToken || "");
      if (jwt) {
        setCusJwtToken(jwt);
        // 綁定可能換了 customer/context → 重設 chooseIdentity；加帳戶模式偏好切到本次入口瓦斯行的新身分。
        // 理由同 ensureRegistered：preferSupplierId 讀 entrySupplierIdSourceRef 單一來源，
        // 不直接讀 entry.supplierId（只帶 shopCode 入口時後者是空字串）。
        await refreshIdentityContexts(jwt, isAddAccount ? entrySupplierIdSourceRef.current : undefined);
      }
      // 結帳聯絡電話優先帶「本次註冊/認領用的電話」（在身分刷新清空之後寫入）；
      // 加帳戶模式這關沒收集手機（digits 只是確認卡未使用的手機 input 殘值，如 "09"），不可當真號碼寫入。
      if (/^09\d{8}$/.test(digits)) setContactPhone(digits);
      finish(false, digits);
    } catch (err) {
      const msg = getRegisterApiErrorMessage(err);
      if (!ticket && barcode) {
        // 終局失敗（不退 OTP）：號碼不符 / 比對驗證失敗（OTP 驗別支號碼也救不了認領）、
        // 嘗試次數過多（後端限流 429）。退 OTP 只會亂發真簡訊。
        if (msg.includes("不符") || msg.includes("比對驗證失敗") || msg.includes("次數過多")) {
          // eslint-disable-next-line no-console
          console.log(`[註冊] barcode 免 OTP 綁定失敗（號碼不符），停留原頁：${msg}`);
          setBindError(msg);
          appToast.error(msg);
          return;
        }
        // 其他失敗（無驗證方式/票證問題等）→ 退回正常 OTP 流程再試一次
        setPhone(digits);
        setOtpForBindRetry(true);
        go("qrOtp");
        return;
      }
      setBindError(msg);
      appToast.error(msg);
    } finally {
      setBinding(false);
    }
  };

  /**
   * OTP 驗證完成後的「手機精確比對」（圖⑦ ①「有找到 100%」）：
   * 打 POST /register/match/by-phone（帶 OTP 票證，後端才回遮罩客戶資料）。
   *   命中     → 帶遮罩客戶資料進「確認客戶資料」(existingPhone)，按「這是我」即免填直接綁定。
   *   沒命中   → 傳單·有指定：問是否叫過桶(npAsk)；QR·不能掃描：改填家用電話(supplierPhone)。
   * 無真實 supplier_id（demo/展示、shopCode 未解析）或無票證/後端錯誤 → 退回原 demo 判斷，不擋展示流程。
   */
  const matchPhoneAndRoute = async (ph: string) => {
    const digits = (ph || "").replace(/\D/g, "");
    const ticket = phoneVerifiedTicket || "";
    const routeNoMatch = () => go(isFlyerKnown ? "npAsk" : "supplierPhone");

    if (entrySupplierId && ticket && /^09\d{8}$/.test(digits)) {
      try {
        const res = await matchByPhone({ accessToken: lineAccessToken || "", supplier_id: entrySupplierId, phone: digits, phone_verified_ticket: ticket });
        const data = res?.data?.data;
        // eslint-disable-next-line no-console
        console.log(`[註冊] 手機比對(match/by-phone) supplier=${entrySupplierId} → ${data?.matched ? `命中 cis_id=${data.customer?.cis_id}` : "未命中 → 往家用電話/新客戶路徑"}`);
        // 地址不完整（沒地址／沒門牌號／拆不出路名）＝這一筆不給認領 → 一律**視為沒命中**，
        // 走原本的沒命中分流（2026-08-12）。不顯示「這是您嗎」：這種資料連門牌號都湊不出來，
        // 客戶在卡上答什麼都只會拿到 HOUSE_NO_UNVERIFIABLE，是死路。
        if (data?.matched && data.customer && !data.customer.address_incomplete) {
          if (data.customer.cis_id) setMatchedCisId(data.customer.cis_id); // 供後續 walk-in 綁定（後端仍會重驗）
          setMatchedCust(
            toMatchedCustomer({
              phone: digits,
              address_masked: data.customer.address_masked,
              addresses: data.customer.addresses,
            }),
          );
          setOtpDone(true); // 已驗過 OTP → 確認卡只顯示客戶資料，不再要求手機/OTP
          setMatchedViaLandline(false); // 這張確認卡來自手機比對，非市話 → reject 維持原行為
          setMatchSource("phone");
          setMatchedLandline(""); // 手機命中 → BE 走 phoneOk 分支，不需市話/地址
          setMatchedAddress("");
          go("existingPhone");
        } else {
          routeNoMatch();
        }
      } catch {
        routeNoMatch(); // 比對失敗（網路/後端錯誤）→ 不擋流程，往新客戶路徑
      }
      return;
    }

    // demo/展示（無真實 supplier_id 或無票證）→ 維持原 demo 清單判斷
    if (isExistingPhone(digits)) { setMatchedCust(findCustomerByPhone(digits)); go("existingPhone"); }
    else routeNoMatch();
  };

  // 進手機閘門（無掃碼比對）：清掉確認卡/原因，先輸入手機。
  // 重置 otpDone：每次重新進手機閘門都從「未 OTP」開始，避免上一輪殘留讓確認卡誤藏手機 input。
  const goExistingPhone = (from: OnbScreen) => {
    setMatchedCust(null);
    setReason(undefined);
    setOtpDone(false);
    setMatchedViaLandline(false);
    setPhoneBackTo(from);
    go("existingPhone");
  };

  // 掃到 barcode → 查 POST /register/cylinder-by-barcode，都進手機閘門：
  // matched → 帶「這是您嗎」確認卡；joined/invalid/new → 帶原因提示。查詢失敗/掃不到 → 不擋流程，當 new 繼續。
  const resolveScan = async (code: string) => {
    setOtpDone(false); // 新掃碼重置 OTP 狀態：比對到客戶的確認卡要重新顯示手機 input
    setMatchedViaLandline(false); // 這張確認卡來自 barcode 掃描，非市話 → reject 維持原行為
    setMatchedLandline(""); // barcode 命中 → BE 走 barcode/phoneOk 分支，不需市話/地址
    setMatchedAddress("");
    setPhoneBackTo("qrBarcode");
    setScannedBarcode(code); // 記下這次掃到的 barcode，供之後免 OTP 綁定（barcode+手機）用
    setScanning(true);
    try {
      const res = await fetchCylinderByBarcode(code, lineAccessToken || "");
      const data = res?.data?.data;
      const state = data?.state ?? "new";
      // QR 無 shopCode 入口的瓦斯行來自「掃到的鋼瓶」：把 barcode 解出的 supplier 存起來，
      // 否則 entrySupplierId 為空 → OTP 後的 match/by-phone 會被跳過（走 demo 分支），
      // 手機比對根本沒打就進「您是這間瓦斯行的客戶嗎?」。
      // 同時填入 supplier 顯示 state（真名），供後續畫面/配對過場顯示，不落入 demo fallback。
      if (data?.supplier?.supplier_id) {
        setEntrySupplierId(data.supplier.supplier_id);
        setSupplier({ name: data.supplier.supplier_name || "", phone: "", area: "", supplier_id: data.supplier.supplier_id });
        // 注意：不在此強制 setEntryCylinderScan(true)。鋼瓶掃描開關一律以瓦斯行真實設定為準
        //（qrGate 的 shopCode 查詢 / FirstJoinSetup 的自我修復查詢），「能掃進來」不代表開關開著
        //（dev 無 shopCode 入口不經 qrGate，開關關閉時也會走到掃描頁）。
      }
      // 地址不完整 → 視為沒命中，落到下方 else（reason=new、不出確認卡、鐵牌重拍）
      if (state === "matched" && data?.customer && !data.customer.address_incomplete) {
        setMatchSource("barcode"); // 這張確認卡來自掃鐵牌命中 → reject 依設計圖①回「輸入手機號碼」
        setPlateCaptured(true); // 只有比對到既有鋼瓶才算擷取鐵牌 → 結帳不再要求拍鐵牌
        if (data.customer.cis_id) setMatchedCisId(data.customer.cis_id); // 供後續 walk-in 綁定（後端仍會重驗）
        setMatchedCust(
          toMatchedCustomer({
            phone: data.customer.phone_masked || "",
            address_masked: data.customer.address_masked,
            addresses: data.customer.addresses,
            hasOrder: !!data.customer.has_recent_order,
          }),
        );
        setReason(undefined);
      } else {
        setPlateCaptured(false); // 查無/失效/已註冊 → 非有效鐵牌，新客戶仍需於結帳拍鐵牌
        setMatchedCust(null);
        setReason(state === "matched" ? "new" : state); // matched 但無 customer（不應發生）→ 保底視為 new
      }
    } catch {
      // 查詢失敗（網路/後端錯誤）→ 不擋流程，當新客戶繼續
      setPlateCaptured(false);
      setMatchedCust(null);
      setReason("new");
    } finally {
      setScanning(false);
    }
    go("existingPhone");
  };

  const render = () => {
    switch (screen) {
      case "home":
        return <Screen title="請由專屬連結進入" subtitle="請透過您收到的鋼瓶 QR 或傳單連結進入。" />;
      // qr 入口帶 shopCode：查詢瓦斯行 cylinder_scan 設定期間的過渡畫面，查完才決定進 qrBarcode 還是 existingPhone(noScan)
      case "qrGate":
        return <Screen title="正在確認掃碼設定" subtitle="請稍候…" />;
      case "qrBarcode":
        return (
          <QrBarcode
            onScan={resolveScan}
            onCantScan={() => goExistingPhone("qrBarcode")}
            // 不給返回鍵：qrBarcode 只由掃碼閘門導入（全專案無其他入口），是流程第一頁。
            // 原本回 "home"，但那頁只是「請透過您收到的 QR 或傳單連結進入」的死巷提示，
            // 沒有任何按鈕能走回流程 → 使用者得重新掃一次 QR 才回得來。
            scanning={scanning}
            showSamples={!entrySupplierId && !entry.shopCode} // 真實入口（已解析瓦斯行）隱藏示範樣本卡
          />
        );
      // 手機比對閘門：輸入手機 → OTP → 確認客戶資料 / 比對不到。
      // otpFirst：只要是 QR barcode 手機閘門（不能掃描 noScan、掃碼失敗 reason、「掃不到改用手機」
      // phoneBackTo==="qrBarcode"，或 URL src=qr），以及傳單·有指定，輸入手機後都先驗 OTP，
      // 不在本頁本地即時比對。避免只靠 entry.src（DevHome/直接 go 進來時可能為 undefined）而漏掉。
      case "existingPhone": {
        const isQrGate = entry.src === "qr" || entry.noScan || !!reason || phoneBackTo === "qrBarcode";
        const otpFirst = !otpDone && (isFlyerKnown || isQrGate);
        return (
          <ExistingPhone
            matched={matchedCust}
            reason={reason}
            requireOtp={otpFirst}
            hidePhoneField={!!matchedCust && otpDone}
            confirmLabel={entry.src === "flyer" ? "這是我，前往主頁" : "這是我，前往下單"}
            confirmBusy={binding}
            errorText={bindError || undefined}
            onDone={(ph, isMatch, houseNo) => {
              // 已於確認卡上「這是我」（不論命中來源）→ 免填直接呼叫 walk-in 綁定 → 完成（傳單→首頁；QR→一鍵下單）
              if (matchedCust || isMatch) {
                setConfirmedHouseNo(houseNo || ""); // 存 state：OTP 補驗迴圈重入時仍取得到
                void bindAndFinish(phone || ph, { houseNo }); // 同事件 state 未 commit → 用 override
                return;
              }
              // 需先驗 OTP 的情況：手機 → 先驗 OTP，再回本頁確認客戶資料 / 比對不到
              if (otpFirst) { setPhone(ph); go("qrOtp"); return; }
              // 門號在既有名單（已於卡上確認）→ 完成
              if (isExistingPhone(ph)) { finish(false, ph); return; }
              // 比對不到 → 改填家用電話
              setPhone(ph);
              go("supplierPhone");
            }}
            /**
             * 「這不是我」——依「這張卡是哪種比對命中的」分流（matchSource），四種來源互不共用。
             * 對照設計圖①（掃 QR）②（傳單·有指定）：
             *   barcode  → 回「輸入手機號碼」重新表明身分
             *   phone    → QR：輸入家用電話(supplierPhone)／傳單·有指定：問「是不是既有客戶」(npAsk)
             *   landline → QR·無指定：當這家新客戶直接進一鍵下單／傳單·有指定：接地址比對
             *   address  → 三關都試過了：標記自稱既有、建新 CIS 掛待核對，直接完成（不再回 npAsk 繞圈）
             * 不可再用 matchedViaLandline／matchedLandline 有無值去反推來源——那會讓四條路互相牽動。
             */
            onReject={(p) => {
              const d = (p || "").replace(/\D/g, "");
              if (/^09\d{8}$/.test(d)) setPhone(d); // 保留真實手機號（確認卡預設 09 不覆蓋）
              // 四種來源共通：先記下被否認的這筆，再分流（分流會 clearMatched，之後就取不到了）
              rejectCis(matchedCisId);
              /** 清掉本次命中的殘留，避免污染下一關比對或誤認領他人 CIS。 */
              const clearMatched = () => {
                setMatchedCust(null);
                setMatchedCisId("");
                setMatchedLandline("");
                setMatchedAddress("");
                setMatchedViaLandline(false);
                setBindError("");
                setMatchSource(null);
                setConfirmedHouseNo(""); // 同理：門牌號只屬於被否認的那張卡，不可帶去驗證下一位命中客戶
              };

              // ── barcode：掃鐵牌命中的不是他 → 回手機號碼頁重新表明身分（設計圖①）
              if (matchSource === "barcode") {
                clearMatched();
                goExistingPhone("qrBarcode");
                return;
              }

              // ── landline（市話）
              if (matchSource === "landline") {
                // QR·無指定：當這家新客戶 → 直接進一鍵下單（不重驗 OTP：結算頁本來就會驗）。
                // 刻意「不清 matchedCust」：finish 內 await ensureRegistered 刷新 session 後才 enterKiosk，
                // session 就緒 kiosk 首次 bootstrap/contexts 才不會 401/403、地址才帶得進；
                // await 期間保留確認卡＋binding 顯示「送出中…」，不會重繪成手機輸入頁閃一下。
                // 四種入口各自表態（不可用某一條的否定代表另一條）
                if (entryKind === "qrUnknown") {
                  // 沒有指定瓦斯行 → 沒有下一關可比對，當這家新客戶直接進一鍵下單
                  //（不重驗 OTP：結算頁本來就會驗）。
                  // 刻意「不清 matchedCust」：finish 內 await ensureRegistered 刷新 session 後才 enterKiosk，
                  // session 就緒 kiosk 首次 bootstrap/contexts 才不會 401/403、地址才帶得進；
                  // await 期間保留確認卡＋binding 顯示「送出中…」，不會重繪成手機輸入頁閃一下。
                  setMatchedViaLandline(false);
                  setMatchedCisId(""); setMatchedLandline(""); setMatchedAddress(""); setBindError("");
                  setMatchSource(null);
                  setBinding(true);
                  void finish(true, phone || d);
                  return;
                }
                if (entryKind === "qrKnown" || entryKind === "flyerKnown") {
                  // 已知瓦斯行 → 還有「地址」這一關可比對，接下去（電話→地址兩段式）。
                  // 回市話頁只會讓客戶重填剛填過的號碼 → 繞圈。
                  clearMatched();
                  setAddrScope("supplier");
                  // 否認的是「那一筆資料」，不是「我不是這家客戶」→ 自稱維持既有
                  setAddrEnding("existingPending");
                  setNpAddrMode("match");
                  go("npAddress");
                  return;
                }
                // flyerUnknown：跨行清單的否認由 NpConfirmSupplier onNo 處理，不會走到這裡；
                // 真的走到就維持原本回市話頁的保底行為。
                clearMatched();
                go("supplierPhone");
                return;
              }

              // ── address（地址）：手機／市話／地址三關都試過都不是他。
              // 他是循「我是這家客戶」的動線一路比對過來的，只是資料比不中 → 標記【自稱既有】直接完成，
              // 建新 CIS 掛待核對（pendingReview），交由 AW 人工比對後合併。
              // 不可回 npAsk：那頁兩個選項是「有叫過→回填市話」「沒叫過→回填地址」，都退回他剛走完的關卡（繞圈）。
              if (matchSource === "address") {
                if (entryKind === "qrKnown") {
                  // QR·有指定：地址是最後一關，否認後沒有更多可比對的了 → 當這家新客戶進一鍵下單。
                  // 不可沿用傳單的收尾（待核對頁／推薦附近瓦斯行）：QR 的瓦斯行已經確定，
                  // 而且 QR 全流程一律以一鍵下單收尾（James 2026-07-20 拍板）。
                  // 不清 matchedCust 的理由同下方傳單分支：await 期間保留卡片避免閃回輸入頁。
                  setMatchedCisId(""); setMatchedLandline(""); setMatchedAddress("");
                  setMatchedViaLandline(false); setBindError(""); setMatchSource(null);
                  setBinding(true);
                  finishQrAfterAddress(phone || d);
                  return;
                }
                if (entryKind === "flyerKnown") {
                  // 刻意不 clearMatched()：finishExistingPending 內要 await ensureRegistered（建 customer，約 1~2 秒），
                  // 若先清掉 matchedCust，確認卡在 await 期間會重繪成「輸入手機號碼」輸入頁，看起來像被踢回上一步。
                  // 改為保留卡片 + binding 顯示「送出中…」，導航後元件卸載自然清除。
                  setMatchedCisId(""); setMatchedLandline(""); setMatchedAddress("");
                  setMatchedViaLandline(false); setBindError(""); setMatchSource(null);
                  setBinding(true);
                  setClaimedExisting(true);
                  void finishExistingPending(phone || d);
                  return;
                }
                clearMatched();
                // 無指定：還不知道所屬瓦斯行 → 依填寫地址推薦附近合作瓦斯行（此路實際由 npConfirmSupplier 承接，此為保底）
                void goNpRecommend(entryAddress || undefined);
                return;
              }

              // ── phone（手機 OTP 命中；null 視同手機，維持既有行為）
              clearMatched();
              if (entry.src === "qr") {
                // 改用家用電話比對/自述身分。但 supplierPhone 三選項的收尾（finish/finishExistingPending）
                // 都靠 ensureRegistered 建 customer，而它需要 OTP 票證＋本人手機；沒票證先回手機閘門驗完再往下，
                // 否則三選項走完不會建 customer、進 kiosk 全 401（2026-07-15 實測）。
                if (!phoneVerifiedTicket) { goExistingPhone("qrBarcode"); return; }
                go("supplierPhone");
                return;
              }
              if (isFlyerKnown) { go("npAsk"); return; } // 傳單·有指定：問「是不是既有客戶」（設計圖②）
              setPhoneBackTo("qrBarcode");
            }}
            onBack={canGoBack ? goBack : undefined}
          />
        );
      }
      // OTP（驗過即 otpVerified，結帳頁不再要求）→ 匹配到：確認客戶資料；匹配不對：輸入家用電話
      case "qrOtp":
        return (
          <NpOtp
            phone={phone}
            onDone={() => {
              setOtpDone(true);
              setOtpVerified(true);
              // barcode 免 OTP 綁定失敗（手機不符/需 OTP）或無任何驗證方式時導來這裡拿票證 → 驗完直接重試綁定，不重新判斷比對
              if (otpForBindRetry) { setOtpForBindRetry(false); void bindAndFinish(phone); return; }
              // 圖⑦ ①：手機精確比對（打真後端 match/by-phone，帶 OTP 票證）→ 命中進確認客戶資料、沒命中往新客戶路徑
              void matchPhoneAndRoute(phone);
            }}
            onBack={canGoBack ? goBack : undefined}
          />
        );
      case "supplierPhone":
        return (
          <SupplierPhone
            triedPhone={phone}
            checking={npLandlineMatching}
            // 「我是這間瓦斯行的客戶」＋填了電話 → 打真後端 match/by-landline 逐組比對
            //（2026-07-15 接真化：原本 demo 名單本地比對，真號碼永遠不中 → ADCUS_252 配不到）。
            // 比對到 → 確認卡（這是我 → walk-in 綁定）；全部沒中 → 自述既有待核對。
            onCheckExisting={async (phones) => {
              if (npLandlineMatching) return;
              setClaimedExisting(true); // 「我是這間瓦斯行的客戶」＋填電話比對
              if (!entrySupplierId) {
                // demo/展示（無真實瓦斯行）→ 維持原行為：直接視為待核對
                void finishExistingPending(phone);
                return;
              }
              setNpLandlineMatching(true);
              try {
                for (const lp of phones) {
                  try {
                    const res = await matchByLandline({ accessToken: lineAccessToken || "", supplier_id: entrySupplierId, landline: lp });
                    const data = res?.data?.data;
                    // eslint-disable-next-line no-console
                    console.log(`[註冊] 市話比對(match/by-landline) ${lp} @${entrySupplierId} → ${data?.matched ? `命中 cis_id=${data.customer?.cis_id}` : "未命中"}`);
                    // 地址不完整 → 視為這一支沒命中（繼續試下一支／最後回到地址比對那一關）
                    if (data?.matched && data.customer && !data.customer.address_incomplete) {
                      if (data.customer.cis_id) setMatchedCisId(data.customer.cis_id); // 供 walk-in 綁定（後端會重驗）
                      setMatchedCust(
                        toMatchedCustomer({
                          phone: data.customer.phone_masked || "",
                          address_masked: data.customer.address_masked,
                          addresses: data.customer.addresses,
                        }),
                      );
                      setOtpDone(true); // 已驗過 OTP → 確認卡只顯示客戶資料
                      setMatchedViaLandline(true);
                      setMatchSource("landline");
                      setMatchedLandline(lp); // 命中依據的「單一」市話，供 walk-in 後端 verifyClaimedCis 驗證
                      setMatchedAddress("");
                      go("existingPhone");
                      return;
                    }
                  } catch {
                    // 這組電話查詢失敗（網路/後端錯誤）→ 略過繼續試下一組
                  }
                }
                // 市話全沒中 → 再給一次機會用「地址」在這家瓦斯行內比對（與傳單·有指定同一套做法）。
                // 刻意在這裡問地址、而不是等到「完成加入設定」才比對：CIS 必須在進 kiosk 前就存在
                //（cart/join/confirm 全都吃 CIS 衍生的 context），綁定後才確認身分就只能靠重綁補救。
                // 地址也沒中 → 由地址頁的 onDone 收尾成「自述既有·待核對」，行為與原本一致。
                setAddrScope("supplier");
                setAddrEnding("existingPending"); // 填了市話比對＝自稱既有，只是比不到
                setNpAddrMode("match");
                go("npAddress");
              } finally {
                setNpLandlineMatching(false);
              }
            }}
            // 兩顆按鈕都先收地址再收尾：地址本來就是送貨必要資料，順便給自稱既有的人再一次比對機會。
            // 收尾（addrEnding）依按鈕明講，不從 claimedExisting 反推。
            onExisting={() => {
              // 忘記/沒登記電話但自稱是這間客戶 → 用地址再比對一次；比不到才待核對
              setClaimedExisting(true);
              setAddrScope("supplier");
              setAddrEnding("existingPending");
              setNpAddrMode("match");
              go("npAddress");
            }}
            onNew={() => {
              // 自稱第一次叫這家 → 不做比對（他已明講不是既有客戶），只收地址，之後當新客戶
              setClaimedExisting(false);
              setAddrScope("supplier");
              setAddrEnding("newCustomer");
              setNpAddrMode("plain");
              go("npAddress");
            }}
            onBack={canGoBack ? goBack : undefined}
          />
        );
      case "adKnown":
        return <AdKnown onNext={() => goExistingPhone("adKnown")} />;

      /* ── 傳單・無指定瓦斯行：手機→OTP→找所屬瓦斯行（家用電話/地址）→ 首頁 / 推薦 ── */
      case "npPhone":
        // 不給返回鍵：npPhone 是傳單·未指定的起始畫面，沒有上一步。
        // 原本回 "home"——那是「請透過您收到的 QR 或傳單連結進入」的死巷提示頁，
        // 沒有任何按鈕能走回流程，使用者只能重開傳單連結。
        return <NpPhone onNext={(ph) => { setPhone(ph); go("npOtp"); }} />;
      case "npOtp":
        return (
          <NpOtp
            phone={phone}
            onDone={async () => {
              // 圖⑦:手機跨行找瓦斯行(真後端 match/suppliers-by-phone,帶 OTP 票證)。
              // 多家→選(npPickSupplier);一家→確認是這家(npConfirmSupplier);沒有→問是否叫過桶(npAsk)。
              const ticket = phoneVerifiedTicket || "";
              const digits = phone.replace(/\D/g, "");
              if (!ticket || !/^09\d{8}$/.test(digits)) { go("npAsk"); return; } // 無票證(展示)→ 問是否叫過桶
              try {
                const res = await matchSuppliersByPhone({ accessToken: lineAccessToken || "", phone: digits, phone_verified_ticket: ticket });
                // 地址不完整的候選直接剔除：那筆不給認領，等同這家沒比中
                //（此處不能直接跳新客戶——還沒決定要掛哪一家瓦斯行，仍須走完找瓦斯行的流程）
                const list = (res?.data?.data?.suppliers ?? []).filter((x) => !x.address_incomplete);
                setNpSupplierMatches(list);
                setMatchedLandline(""); // 手機跨行命中 → BE 走 phoneOk 分支，不需市話/地址
                setMatchedAddress("");
                setNpConfirmSource("phone");
                if (list.length > 1) go("npPickSupplier");
                else if (list.length === 1) { setNpConfirmIdx(0); go("npConfirmSupplier"); }
                else { go("npAsk"); }
              } catch {
              }
            }}
            onBack={canGoBack ? goBack : undefined}
          />
        );
      case "npPickSupplier":
        // 比對到多家 → 選所屬那家（最近叫桶排序）→ 進確認卡（顯示該家＋客戶資料）再確認
        return (
          <NpPickSupplier
            matches={npSupplierMatches.map((s) => ({ id: s.supplier_id, name: s.supplier_name, area: s.supplier_address || "", phone: s.supplier_phone || "" }))}
            onPick={(sid) => { const idx = npSupplierMatches.findIndex((x) => x.supplier_id === sid); if (idx >= 0) { setNpConfirmIdx(idx); go("npConfirmSupplier"); } }}
            onBack={canGoBack ? goBack : undefined}
          />
        );
      case "npConfirmSupplier": {
        const cs = npSupplierMatches[npConfirmIdx];
        const csAddr = cs && "address_masked" in cs ? cs.address_masked || "" : "";
        const csPhone = cs && "phone_masked" in cs ? cs.phone_masked || "" : "";
        return (
          <NpConfirmSupplier
            supplier={{ name: cs?.supplier_name || "", area: cs?.supplier_address || "", phone: cs?.supplier_phone || "", supplier_id: cs?.supplier_id || "" }}
            custPhone={csPhone}
            custAddr={csAddr}
            busy={binding}
            // 是，就是這家＝承認屬於這家＋確認上方就是本人資料 → 直接綁定既有客戶（walk-in）→ 歡迎回來/首頁。
            // 剛 setEntrySupplierId/setMatchedCisId 後同一事件呼叫 bindAndFinish，closure 讀舊值 → 用 override 帶新值。
            onYes={(houseNo) => {
              if (!cs) return;
              setClaimedExisting(true);
              setEntrySupplierId(cs.supplier_id);
              setMatchedCisId(cs.cis_id);
              setConfirmedHouseNo(houseNo || ""); // 存 state：OTP 補驗迴圈重入 bindAndFinish 時仍取得到
              void bindAndFinish(phone, { supplierId: cs.supplier_id, cisId: cs.cis_id, houseNo });
            }}
            onNo={() => {
              rejectCis(cs?.cis_id);
              setConfirmedHouseNo(""); // 門牌號只屬於被否認的那張卡，不可帶去驗證下一家/下一位
              // 不是這家：依「進這張卡的比對來源」分流，三來源不可一律（否則耦合）
              if (npConfirmSource === "landline") { setAddrScope("cross"); setNpAddrMode("match"); go("npAddress"); } // 家用電話→地址頁跨行找真正所屬瓦斯行
              else if (npConfirmSource === "address") { void goNpRecommend(matchedAddress || entryAddress || undefined); } // 地址→推薦附近瓦斯行（避免又回地址頁繞圈）
              else {
                // 手機→原始切版：問是否叫過桶。返回要回到剛剛那張確認卡（多家則回選單），
                // 不可沿用 npAsk 預設的「回 OTP 頁」——使用者已經驗過，回去只會被要求重驗。
                go("npAsk");
              }
            }}
            onBack={canGoBack ? goBack : undefined}
          />
        );
      }
      case "npAsk":
        return (
          <NpAsk
            title={isFlyerKnown ? <>您有在 {supplier.name}<br />叫過桶裝瓦斯嗎？</> : undefined}
            subtitle={isFlyerKnown ? "確認您是否為這家的既有客戶" : undefined}
            onYes={() => { setClaimedExisting(true); go("npHomePhone"); }}
            onNo={() => {
              // 沒叫過＝新客戶：只收地址不比對。scope 仍要明講——已知瓦斯行（傳單·有指定）
              // 走「完成註冊→首頁」，未指定才走「推薦附近瓦斯行」。
              // 漏設的後果：sticky 的 addrScope 預設 "cross"，讓 supplier=GS_1 的傳單客戶
              // 也跑去推薦附近瓦斯行 → 查無 → 卡在「正在為您安排瓦斯行」。
              setClaimedExisting(false);
              setAddrScope(entryKind === "flyerKnown" || entryKind === "qrKnown" ? "supplier" : "cross");
              setAddrEnding("newCustomer");
              setNpAddrMode("plain");
              go("npAddress");
            }}
            onBack={canGoBack ? goBack : undefined}
          />
        );
      case "npHomePhone":
        return (
          <NpHomePhone
            supplierKnown={isFlyerKnown}
            matching={npLandlineMatching}
            // 先用家用電話（市話）逐一比對真後端；比對不到才進地址頁改用地址比對（npMatchLandlineAndRoute）
            onSubmit={(landlines) => { void npMatchLandlineAndRoute(landlines); }}
            onBack={canGoBack ? goBack : undefined}
          />
        );
      case "npAddress":
        return (
          <NpAddress
            mode={npAddrMode}
            // 已知瓦斯行（傳單·有指定 或 QR）→ 文案是「填寫配送地址」，不是「用地址為您尋找所屬瓦斯行」
            supplierKnown={addrScope === "supplier"}
            prefillAddr={locateAllowed ? locAddrLine : undefined}
            prefillDoor={locateAllowed ? locAddrDoor : undefined}
            prefillLat={locateAllowed ? locLat : undefined}
            prefillLng={locateAllowed ? locLng : undefined}
            skipLocate={locateAllowed}
            busy={addrMatching}
            onDone={async (addr, mapLat, mapLng, note) => {
              // 使用者在地址步驟實際填的地址，存進「持久」的 kiosk store（entryAddress），
              // 供之後「完成加入設定」(FirstJoinSetup) 組 walk-in body 使用。
              // 註：不可存 chooseIdentity.choose_address —— 那個 store 導航到首頁時會被
              // /customer/contexts 重列洗掉（見 memory reference_identity_choose_address_loss），
              // 地址遺失 → walk-in address 空 → 後端「新客戶必須填寫地址」400。
              // entryLandline（市話）就是走這個持久 store 才帶得到，地址比照辦理。
              setEntryAddress(addr);
              setEntryAddressNote(note ?? "");
              // 已知瓦斯行（傳單·有指定 或 QR 掃碼）：家用電話已比對過沒中 → 打 POST /register/match/by-address
              // 在「這一家」內做純地址比對。用 addrScope 而非 isFlyerKnown：QR 也走同一條，
              // 反推會讓新增一條路就悄悄改變既有路的行為。
              if (addrScope === "supplier") {
                if (npAddrMode === "match") {
                  if (addrMatching) return; // 避免重複送出
                  // 有真實 supplier_id（shopCode 已解析）才能打真後端比對；沒有則維持 demo fallback，不擋展示流程
                  if (entrySupplierId) {
                    setAddrMatching(true);
                    try {
                      const res = await matchByAddress({
                        accessToken: lineAccessToken || "",
                        supplier_id: entrySupplierId,
                        address: addr,
                      });
                      const data = res?.data?.data;
                      // 地址不完整 → 視為沒命中，走下方原本的收尾分流（QR 一鍵下單／傳單「沒有找到您」）
                      if (data?.matched && data.customer && !data.customer.address_incomplete) {
                        if (data.customer.cis_id) setMatchedCisId(data.customer.cis_id); // 供後續 walk-in 綁定（後端仍會重驗）
                        setMatchedCust(
                          toMatchedCustomer({
                            phone: data.customer.phone_masked || "",
                            address_masked: data.customer.address_masked || addr,
                            addresses: data.customer.addresses,
                          }),
                        );
                        setOtpDone(true); // 確認卡只顯示客戶資料
                        setMatchSource("address");
                        setMatchedViaLandline(false); // 既有漏設：不清會殘留上一關市話的 true，害地址來源被誤判成市話
                        setMatchedAddress(addr); // 記下這個「比對成功」的地址，供 bindAndFinish 送後端驗證
                        setMatchedLandline("");
                        go("existingPhone");
                      } else {
                        // 收尾依入口分流：QR 一律進一鍵下單；傳單維持首頁「沒有找到您」卡。
                        if (entryKind === "qrKnown") finishQrAfterAddress(phone);
                        else goNotFoundHome(); // 傳單·有指定：首頁「沒有找到您」卡（dev 新流程）
                      }
                    } catch {
                      // 比對失敗（網路/後端錯誤）→ 不擋流程，收尾同樣依入口分流
                      if (entryKind === "qrKnown") finishQrAfterAddress(phone);
                      else goNotFoundHome();
                    } finally {
                      setAddrMatching(false);
                    }
                    return;
                  }
                  // demo fallback（無真實 supplier_id / shopCode 未解析）
                  if (isKnownAddress(addr)) {
                    setMatchedCust(findCustomerByPhone("0916375136")); // demo：以地址比對到的既有客戶
                    setOtpDone(true);
                    go("existingPhone");
                  } else { goNotFoundHome(); }
                  return;
                }
                setAddrMatching(true); // 「完成」按鈕轉圈（登記為 async）；導航後自然消失
                // plain（沒叫過的新客戶，只登記地址）收尾依入口分流，與上方 match 分支一致：
                // 傳單 → 完成註冊過場 → 首頁；QR → 不播完成頁，直接進一鍵下單（finish 內建此分流）。
                await finish(true, phone);
                return;
              }
              // 傳單·無指定(有叫過桶 match 模式，家用電話已在 npHomePhone 比對過沒中)→ 純地址跨行找瓦斯行(真後端 match/suppliers-by-address,免 OTP)
              if (npAddrMode === "match") {
                if (addrMatching) return;
                setAddrMatching(true);
                try {
                  const res = await matchSuppliersByAddress({ accessToken: lineAccessToken || "", address: addr });
                  // 地址不完整的候選剔除（同 suppliers-by-phone 的理由）
                  const list = (res?.data?.data?.suppliers ?? []).filter((x) => !x.address_incomplete);
                  setNpSupplierMatches(list);
                  // 記下這個「比對成功」的地址：稍後在 npConfirmSupplier「是這家」onYes 送 bindAndFinish 時要用。
                  setMatchedAddress(addr);
                  setMatchedLandline("");
                  setNpConfirmSource("address");
                  if (list.length > 1) go("npPickSupplier");
                  else if (list.length === 1) { setNpConfirmIdx(0); go("npConfirmSupplier"); }
                  // 地址也沒配到 → 依座標推薦附近合作瓦斯行讓客戶自選（真後端，取代 demo 假配對）
                  else void goNpRecommend(addr, mapLat, mapLng);
                } catch {
                  void goNpRecommend(addr, mapLat, mapLng);
                } finally {
                  setAddrMatching(false);
                }
              } else if (entryKind === "qrKnown") {
                // QR·有指定 plain：瓦斯行已由掃碼確定，不需要也不可以再推薦別家 → 直接當新客戶進一鍵下單
                setAddrMatching(true);
                finishQrAfterAddress(phone);
              } else {
                setAddrMatching(true); // 「完成」按鈕轉圈；goNpRecommend 導航（配對動畫/pending）後自然消失
                void goNpRecommend(addr, mapLat, mapLng); // plain(沒叫過) → 推薦附近合作瓦斯行
              }
            }}
            onBack={canGoBack ? goBack : undefined}
          />
        );
      case "npAutoMatched":
        // 傳單·無指定一路沒配到 → 自動配對最近瓦斯行；純過場，顯示配到哪一家後自動繼續（不需確認、不換家）
        // 完成註冊過場（celebrateThen 設了落點 ref）→ 顯示「真實」supplier、過場結束直接進 npDone；
        // 舊無指定路徑（未設 ref）→ supplier 有真值（shopCode/barcode 已解析）優先用真值，
        // 完全沒有真瓦斯行（純無指定，推薦後端未做）才落 demo 表——正式流程不可顯示 mock 名稱。
        // 查無合作瓦斯行：只播「配對中」雷達（不揭示 supplier）→ onDone 進等待頁 pending（npDoneVariant 已於 goNpRecommend 設好）
        if (npAutoNoMatch) {
          return <NpAutoMatched noMatch onDone={() => { setNpAutoNoMatch(false); go("npDone"); }} />;
        }
        return npDoneNextRef.current ? (
          <NpAutoMatched supplier={supplier} onDone={() => go("npDone")} />
        ) : (
          <NpAutoMatched supplier={supplier.supplier_id ? supplier : getSupplier(matchedSupplierId)} onDone={confirmAutoMatched} />
        );
      case "npDone":
        return (
          <NpDone
            variant={npDoneVariant}
            cta={npDoneCta}
            onGoHome={async () => {
              await ensureRegistered(); // 冪等：所有完成路徑離開前保證 customer 已建（階段一 401 fix）
              const next = npDoneNextRef.current;
              npDoneNextRef.current = null;
              (next ?? enterHome)(); // 未設落點（既有無指定路徑）→ 維持原行為進首頁
            }}
          />
        );
      default:
        return <Screen title="請由專屬連結進入" subtitle="請透過您收到的鋼瓶 QR 或傳單連結進入。" />;
    }
  };

  // 已註冊閘門查詢中（或即將導走）→ 過渡畫面，不渲染 onboarding 流程（避免閃一下又跳走）
  if (regGate === "checking") {
    return <Screen title="正在確認帳號狀態" subtitle="請稍候…" />;
  }

  return (
    <SupplierContext.Provider value={supplier}>
      <div className="h-full w-full">
        {render()}

        {/* 只在「已知拿不到位置」時出現的說明卡；正常流程是系統窗直接跳，這張不會顯示 */}
        <LocateSheet
          open={locateAsk}
          locating={locating}
          blocked={locateBlocked}
          // 卡片模式要當場給結果：拿到就關掉往下走，仍被擋就換成「怎麼救」的畫面，
          // 不能像背景模式那樣按完就關 —— 使用者是為了這件事按的，得看到結論。
          onAllow={() => {
            void applyCurrentLocation({ silent: true }).then((ok) => {
              if (ok) setLocateAsk(false);
              else setLocateBlocked(true);
            });
          }}
          onManual={() => setLocateAsk(false)}
        />
      </div>
    </SupplierContext.Provider>
  );
};

export default Onboarding;
